import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { toast } from 'react-hot-toast';
import type { OsKanbanNavigationState } from '../types/ordemServico';
import EtiquetasModal from '../components/EtiquetasModal';

interface FaseOS {
  id: number | null;
  nome_fase: string;
  status_fase: string;
  ordem: number;
  endereco_destino: string;
  id_motorista_atribuido?: number;
  motorista?: {
    id: number;
    nome: string;
    email: string;
  };
  veiculo?: {
    placa: string;
    prefixo?: string | null;
    marca?: string | null;
    modelo?: string | null;
  };
  data_fase?: string | null;
  horario?: string | null;
  nome_local?: string | null;
}

interface FaseForm {
  nome_fase: string;
  ordem: number;
  endereco_destino: string;
  nome_local?: string;
  data?: string;
  horario?: string;
}

interface OrdemServico {
  id: number | null;
  codigo_acesso: string | null;
  nome_falecido: string;
  numero_declaracao_obito: string | null;
  numero_nota_contratacao: string;
  status_geral: string;
  observacoes?: string;
  criado_em: string;
  fases: FaseOS[];
  // Campos para OSs do Sisfuner não importadas
  isSisfuner?: boolean;
  _DADOSOS_NumOrdem?: string;
  _DADOSOS_DataInsert?: string;
  velorio_sepultamento_mesmo_local?: boolean;
  origem?: 'manual' | 'sisfuner';
  contratante_nome?: string | null;
  contratante_telefone1?: string | null;
  contratante_telefone2?: string | null;
}

interface NotificacaoWhatsApp {
  id: number;
  telefone: string | null;
  status: 'enviado' | 'erro' | 'ignorado';
  automatico: boolean;
  erro: string | null;
  criado_em: string;
  usuario_nome: string | null;
}

const NOTIFICACAO_STATUS_LABEL: Record<NotificacaoWhatsApp['status'], { label: string; className: string }> = {
  enviado: { label: 'Enviado', className: 'bg-green-100 text-green-800' },
  erro: { label: 'Erro', className: 'bg-red-100 text-red-800' },
  ignorado: { label: 'Não enviado', className: 'bg-yellow-100 text-yellow-800' }
};

const OrdensServico: React.FC = () => {
  console.log('🔍 Componente OrdensServico renderizando...');
  const location = useLocation();
  const navigate = useNavigate();
  const kanbanActionHandledRef = useRef(false);
  const returnToKanbanRef = useRef(false);
  const createFormRef = useRef<HTMLDivElement>(null);
  const shouldScrollToCreateFormRef = useRef(false);

  const voltarParaKanban = () => {
    if (!returnToKanbanRef.current) return;
    returnToKanbanRef.current = false;
    navigate('/os-kanban');
  };

  const scrollToCreateForm = () => {
    requestAnimationFrame(() => {
      createFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const resetCreateForm = () => {
    setFormData({
      nome_falecido: '',
      numero_declaracao_obito: '',
      numero_nota_contratacao: '',
      observacoes: '',
      fases: [
        { nome_fase: 'Remoção', ordem: 1, endereco_destino: '' },
        { nome_fase: 'Clínica', ordem: 2, endereco_destino: '' },
        { nome_fase: 'Velório', ordem: 3, endereco_destino: '' },
        { nome_fase: 'Sepultamento', ordem: 4, endereco_destino: '' }
      ],
      isSisfuner: false,
      velorio_sepultamento_mesmo_local: false
    });
  };

  const cancelCreateForm = () => {
    setShowCreateForm(false);
    setSelectedOS(null);
    resetCreateForm();
    voltarParaKanban();
  };

  const closeMotoristaModal = () => {
    setSelectedOS(null);
    setSelectedFase('');
    setSelectedMotorista(0);
    setSelectedCMV(null);
    setCmvFilters({
      data: new Date().toISOString().split('T')[0],
      turno: '',
      search: ''
    });
    setCmvs([]);
    setShowCMVList(false);
    setShowMotoristaAssignPrompt(false);
    setLastAssignedFaseNome(null);
    voltarParaKanban();
  };

  useEffect(() => {
    if (location.pathname !== '/ordens-servico') {
      returnToKanbanRef.current = false;
    }
  }, [location.pathname]);

  // Função para obter a cor de fundo baseada no tipo de fase
  const getFaseBackgroundColor = (nomeFase: string): string => {
    switch (nomeFase) {
      case 'Remoção':
        return 'bg-blue-50 border-blue-200'; // Azul claro para ambulância/emergência
      case 'Clínica':
        return 'bg-green-50 border-green-200'; // Verde claro para saúde/hospital
      case 'Velório':
        return 'bg-purple-50 border-purple-200'; // Roxo claro para cerimônia/solene
      case 'Sepultamento':
        return 'bg-gray-100 border-gray-300'; // Cinza claro para cemitério
      default:
        return 'bg-gray-50 border-gray-200'; // Cor padrão
    }
  };

  const formatarVeiculo = (veiculo?: { placa: string; prefixo?: string | null; marca?: string | null; modelo?: string | null } | null): string => {
    if (!veiculo) return '';
    
    const marcaModelo = veiculo.marca && veiculo.modelo 
      ? `${veiculo.marca} ${veiculo.modelo}`
      : veiculo.marca 
        ? veiculo.marca
        : veiculo.modelo 
          ? veiculo.modelo
          : '';
    
    if (marcaModelo) {
      return `${marcaModelo} (${veiculo.placa})`;
    }
    
    return veiculo.placa;
  };

  const podeExcluirFase = (fase: FaseOS): boolean =>
    fase.status_fase === 'Pendente' && !fase.id_motorista_atribuido && !fase.motorista;

  const reordenarFasesLocal = (fases: FaseOS[]): FaseOS[] =>
    fases.map((fase, index) => ({ ...fase, ordem: index + 1 }));

  const possuiFasesVelorioSepultamento = (fases: Array<{ nome_fase: string }>) =>
    fases.some((f) => f.nome_fase === 'Velório') && fases.some((f) => f.nome_fase === 'Sepultamento');

  const fasesVelorioSepultamentoIniciadas = (fases: FaseOS[]) => {
    const velorio = fases.find((f) => f.nome_fase === 'Velório');
    const sepultamento = fases.find((f) => f.nome_fase === 'Sepultamento');
    const iniciada = (f?: FaseOS) =>
      !!f && (f.status_fase === 'Em Rota' || f.status_fase === 'Concluída' || !!f.timestamp_inicio_rota);
    return iniciada(velorio) || iniciada(sepultamento);
  };

  const renderCheckboxVelorioSepultamentoMesmoLocal = (
    checked: boolean,
    onChange: (checked: boolean) => void,
    options?: { disabled?: boolean; showLockedMessage?: boolean }
  ) => (
    <div className="my-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
      <label className={`flex items-start gap-2 ${options?.disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
        <input
          type="checkbox"
          checked={checked}
          disabled={options?.disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1"
        />
        <span className="text-sm text-gray-800">
          <span className="font-medium">Velório e sepultamento no mesmo local</span>
          <span className="block text-xs text-gray-600 mt-1">
            O motorista executará apenas o Velório. Sepultamento será concluído automaticamente ao finalizar o velório.
            {options?.showLockedMessage &&
              ' Não pode alterar após Velório ou Sepultamento iniciados.'}
          </span>
        </span>
      </label>
    </div>
  );

  const [ordens, setOrdens] = useState<OrdemServico[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedOS, setSelectedOS] = useState<OrdemServico | null>(null);

  useEffect(() => {
    if (showCreateForm && shouldScrollToCreateFormRef.current) {
      shouldScrollToCreateFormRef.current = false;
      scrollToCreateForm();
    }
  }, [showCreateForm]);

  const [showViewModal, setShowViewModal] = useState(false);
  const [notificacoesWhatsApp, setNotificacoesWhatsApp] = useState<NotificacaoWhatsApp[]>([]);
  const [loadingWhatsApp, setLoadingWhatsApp] = useState(false);
  const [reenviandoWhatsApp, setReenviandoWhatsApp] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState<OrdemServico | null>(null);
  const [motoristas, setMotoristas] = useState<Array<{id: number, nome: string, email: string}>>([]);
  const [selectedFase, setSelectedFase] = useState<string>('');
  const [selectedMotorista, setSelectedMotorista] = useState<number>(0);
  const [isFetching, setIsFetching] = useState(false);
  // Estados para CMV
  const [cmvs, setCmvs] = useState<Array<{
    id: number;
    cmv: number;
    data: string;
    polo: string;
    turno: string;
    veiculo_id: number;
    veiculo_placa: string;
    veiculo_prefixo: string;
    veiculo_marca: string;
    veiculo_modelo: string;
    motorista_id: number;
    motorista_nome: string;
    motorista_matricula: string;
  }>>([]);
  const [selectedCMV, setSelectedCMV] = useState<number | null>(null);
  const [cmvFilters, setCmvFilters] = useState<{
    data: string;
    turno: string;
    search: string; // Campo único para buscar por nome do motorista, placa ou prefixo
  }>({
    data: new Date().toISOString().split('T')[0], // Hoje como padrão
    turno: '',
    search: ''
  });
  const [isLoadingCMVs, setIsLoadingCMVs] = useState(false);
  const [showCMVList, setShowCMVList] = useState(false);
  const [showMotoristaAssignPrompt, setShowMotoristaAssignPrompt] = useState(false);
  const [lastAssignedFaseNome, setLastAssignedFaseNome] = useState<string | null>(null);
  const [isAssigningMotorista, setIsAssigningMotorista] = useState(false);
  const [etiquetaOS, setEtiquetaOS] = useState<OrdemServico | null>(null);
  // Estado para controlar qual tab está ativa
  // Inicia na tab de pendentes se houver, senão inicia na de monitoramento
  const [activeTab, setActiveTab] = useState<'pendentes' | 'monitoramento'>(() => {
    // Será atualizado após o primeiro carregamento
    return 'pendentes';
  });
  
  // Estado para controlar se mostra todas as OSs em monitoramento ou apenas as não concluídas
  const [mostrarTodasOSs, setMostrarTodasOSs] = useState(false);
  
  // Paginação para OSs sem monitoramento
  const [currentPageSemMonitoramento, setCurrentPageSemMonitoramento] = useState(1);
  // Paginação para OSs com monitoramento
  const [currentPageComMonitoramento, setCurrentPageComMonitoramento] = useState(1);
  const [itemsPerPage] = useState(15); // Número de itens por página
  // Ref para controlar alertas já notificados (evitar spam de toasts)
  const alertasNotificadosRef = useRef<Set<string>>(new Set());
  const hasLoadedOrdensRef = useRef(false);
  
  const [formData, setFormData] = useState<{
    nome_falecido: string;
    numero_declaracao_obito: string;
    numero_nota_contratacao: string;
    observacoes: string;
    fases: FaseForm[];
    isSisfuner?: boolean;
    numeroOrdemSisfuner?: string;
    velorio_sepultamento_mesmo_local?: boolean;
    contratante_nome?: string;
    contratante_telefone1?: string;
    contratante_telefone2?: string;
  }>({
    nome_falecido: '',
    numero_declaracao_obito: '',
    numero_nota_contratacao: '',
    observacoes: '',
    fases: [],
    isSisfuner: false,
    velorio_sepultamento_mesmo_local: false
  });

  // Garantir que ordens seja sempre um array
  const safeOrdens = Array.isArray(ordens) ? ordens : [];
  
  // Separar OSs em dois grupos
  // OSs pendentes: Apenas OSs do Sisfuner NÃO importadas
  const ordensSemMonitoramento = safeOrdens.filter(os => 
    os.isSisfuner === true
  );
  // OSs em monitoramento: Todas as OSs que já estão no banco do Nexus (independente do status)
  const ordensComMonitoramentoBase = safeOrdens.filter(os => 
    os.isSisfuner !== true
  );
  
  // Filtrar OSs em monitoramento baseado no estado mostrarTodasOSs
  const ordensComMonitoramento = mostrarTodasOSs 
    ? ordensComMonitoramentoBase 
    : ordensComMonitoramentoBase.filter(os => os.status_geral !== 'Concluída');
  
  // Atualizar tab padrão quando os dados carregarem
  useEffect(() => {
    if (ordensSemMonitoramento.length === 0 && ordensComMonitoramentoBase.length > 0 && activeTab === 'pendentes') {
      setActiveTab('monitoramento');
    } else if (ordensSemMonitoramento.length > 0 && activeTab === 'monitoramento' && ordensComMonitoramentoBase.length === 0) {
      setActiveTab('pendentes');
    }
  }, [ordensSemMonitoramento.length, ordensComMonitoramentoBase.length, activeTab]);
  
  // Calcular paginação para OSs sem monitoramento
  const totalPagesSemMonitoramento = Math.ceil(ordensSemMonitoramento.length / itemsPerPage);
  const startIndexSemMonitoramento = (currentPageSemMonitoramento - 1) * itemsPerPage;
  const endIndexSemMonitoramento = startIndexSemMonitoramento + itemsPerPage;
  const currentOrdensSemMonitoramento = ordensSemMonitoramento.slice(startIndexSemMonitoramento, endIndexSemMonitoramento);
  
  // Calcular paginação para OSs com monitoramento
  const totalPagesComMonitoramento = Math.ceil(ordensComMonitoramento.length / itemsPerPage);
  const startIndexComMonitoramento = (currentPageComMonitoramento - 1) * itemsPerPage;
  const endIndexComMonitoramento = startIndexComMonitoramento + itemsPerPage;
  const currentOrdensComMonitoramento = ordensComMonitoramento.slice(startIndexComMonitoramento, endIndexComMonitoramento);
  
  // Resetar páginas quando as listas mudarem
  useEffect(() => {
    if (currentPageSemMonitoramento > totalPagesSemMonitoramento && totalPagesSemMonitoramento > 0) {
      setCurrentPageSemMonitoramento(1);
    }
  }, [ordensSemMonitoramento.length, currentPageSemMonitoramento, totalPagesSemMonitoramento]);
  
  useEffect(() => {
    if (currentPageComMonitoramento > totalPagesComMonitoramento && totalPagesComMonitoramento > 0) {
      setCurrentPageComMonitoramento(1);
    }
  }, [ordensComMonitoramento.length, currentPageComMonitoramento, totalPagesComMonitoramento]);

  // Resetar paginação quando o filtro mudar
  useEffect(() => {
    setCurrentPageComMonitoramento(1);
  }, [mostrarTodasOSs]);

  // Buscar CMVs automaticamente quando os filtros mudarem (com debounce para o campo de busca)
  useEffect(() => {
    if (selectedOS && !showViewModal && !showEditModal) {
      const timer = setTimeout(() => {
        fetchCMVs();
      }, cmvFilters.search ? 500 : 0); // Debounce de 500ms apenas para o campo de busca
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmvFilters.data, cmvFilters.turno, cmvFilters.search, selectedOS]);

  // Fechar lista de CMVs ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showCMVList && !target.closest('.cmv-search-container')) {
        setShowCMVList(false);
      }
    };

    if (showCMVList) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCMVList]);

  useEffect(() => {
    // Adicionar delay para evitar muitas requisições simultâneas
    const timer = setTimeout(() => {
      fetchOrdens();
      fetchMotoristas();
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Atualização automática a cada 2 minutos para OSs e alertas
  useEffect(() => {
    // Intervalo de 2 minutos (120000 ms)
    const intervalId = setInterval(() => {
      console.log('🔄 Atualização automática das OSs...');
      fetchOrdens({ silent: true });
    }, 2 * 60 * 1000);

    // Limpar intervalo quando o componente for desmontado
    return () => clearInterval(intervalId);
  }, []); // Executar apenas uma vez ao montar o componente

  // Mantém a lista atual sem redisparar toasts a cada refresh/atribuição
  const ordensMonitoramentoRef = useRef(ordensComMonitoramentoBase);
  ordensMonitoramentoRef.current = ordensComMonitoramentoBase;

  // Toasts de atraso/próxima: só ao ENTRAR na aba Em Monitoramento (uma vez por visita)
  useEffect(() => {
    if (activeTab !== 'monitoramento') {
      alertasNotificadosRef.current.clear();
      return;
    }

    type AlertaPendente = {
      mensagem: string;
      status: 'proxima' | 'atrasada';
    };

    const novosAlertas: AlertaPendente[] = [];

    ordensMonitoramentoRef.current.forEach(os => {
      if (os.isSisfuner) return;

      const { alertas } = getOSAlertas(os);

      alertas.forEach(({ fase, status }) => {
        const alertaKey = `${os.id}-${fase.id}-${status}`;
        if (alertasNotificadosRef.current.has(alertaKey)) return;
        alertasNotificadosRef.current.add(alertaKey);

        const tempo = formatarTempoRestante(fase);
        if (status === 'atrasada') {
          novosAlertas.push({
            status,
            mensagem: `⚠️ Fase atrasada! OS ${os.codigo_acesso || os.id}: ${fase.nome_fase} ${tempo ? `(${tempo})` : ''}`
          });
        } else if (status === 'proxima') {
          novosAlertas.push({
            status,
            mensagem: `⏰ Fase próxima! OS ${os.codigo_acesso || os.id}: ${fase.nome_fase} ${tempo ? `(${tempo})` : ''}`
          });
        }
      });
    });

    novosAlertas.forEach((alerta, index) => {
      const isTopo = index === novosAlertas.length - 1;
      const fecharTodos = (e: React.MouseEvent) => {
        e.stopPropagation();
        toast.dismiss();
      };

      const corpo = () => (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 220 }}>
          <span style={{ flex: 1, fontSize: 14, lineHeight: 1.35 }}>{alerta.mensagem}</span>
          {isTopo && (
            <button
              type="button"
              onClick={fecharTodos}
              aria-label="Fechar todos"
              title="Fechar todos"
              style={{
                flexShrink: 0,
                width: 24,
                height: 24,
                borderRadius: 9999,
                border: 'none',
                cursor: 'pointer',
                fontSize: 16,
                lineHeight: '24px',
                fontWeight: 700,
                background: alerta.status === 'atrasada' ? 'rgba(255,255,255,0.25)' : 'rgba(146,64,14,0.15)',
                color: alerta.status === 'atrasada' ? '#fff' : '#92400E',
              }}
            >
              ×
            </button>
          )}
        </div>
      );

      if (alerta.status === 'atrasada') {
        toast.error(corpo, { duration: 8000 });
      } else {
        toast(corpo, {
          icon: '⏰',
          duration: 6000,
          style: {
            background: '#FEF3C7',
            color: '#92400E',
          }
        });
      }
    });

    // Ao sair da aba, limpa o controle para poder avisar de novo na próxima entrada
    return () => {
      alertasNotificadosRef.current.clear();
      toast.dismiss();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchOrdens = async (options?: { silent?: boolean }) => {
    if (isFetching) {
      console.log('⚠️ Requisição já em andamento, ignorando...');
      return;
    }
    
    try {
      setIsFetching(true);
      if (!hasLoadedOrdensRef.current && !options?.silent) {
        setLoading(true);
      }
      
      console.log('🔍 Fazendo requisição para /os...');
      const response = await api.get('/os', { timeout: 60000 });
      
      // A API retorna {os: [...], pagination: {...}}, precisamos extrair o array 'os'
      const ordensData = response.data?.os || [];
      console.log('🔍 Dados brutos da API:', response.data);
      console.log('🔍 Array extraído:', ordensData);
      
      setOrdens(ordensData);
      hasLoadedOrdensRef.current = true;
    } catch (error: any) {
      console.error('❌ Erro ao carregar ordens:', error);
      
      if (error.response?.status === 429) {
        toast.error('Muitas requisições. Aguarde um momento...');
        // Aguardar 5 segundos antes de tentar novamente
        setTimeout(() => {
          setIsFetching(false);
        }, 5000);
      } else if (!hasLoadedOrdensRef.current) {
        toast.error('Erro ao carregar ordens de serviço');
        setOrdens([]); // Garantir que ordens seja sempre um array
      } else {
        toast.error('Erro ao atualizar. Mantendo os dados anteriores.');
      }
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const fetchMotoristas = async () => {
    try {
      console.log('🔍 Fazendo requisição para /usuarios/motoristas...');
      const response = await api.get('/usuarios/motoristas');
      console.log('✅ Resposta da API motoristas:', response.data);
      
      const motoristasData = response.data.motoristas || [];
      console.log('✅ Motoristas extraídos:', motoristasData);
      
      setMotoristas(motoristasData);
    } catch (error: any) {
      console.error('❌ Erro ao carregar motoristas:', error);
      console.error('❌ Status:', error.response?.status);
      console.error('❌ Dados:', error.response?.data);
      
      if (error.response?.status === 429) {
        console.log('⚠️ Rate limit atingido para motoristas, tentando novamente em 10s...');
        // Tentar novamente em 10 segundos
        setTimeout(() => {
          fetchMotoristas();
        }, 10000);
      }
    }
  };

  const fetchCMVs = async () => {
    try {
      setIsLoadingCMVs(true);
      const params = new URLSearchParams();
      if (cmvFilters.data) params.append('data', cmvFilters.data);
      if (cmvFilters.turno) params.append('turno', cmvFilters.turno);
      
      // O campo de busca pode ser nome do motorista, placa ou prefixo
      // O backend vai buscar em todos os campos
      if (cmvFilters.search) {
        params.append('search', cmvFilters.search);
      }
      
      params.append('limit', '100'); // Limite maior para seleção

      const response = await api.get(`/cmv?${params.toString()}`);
      setCmvs(response.data.cmvs || []);
    } catch (error: any) {
      console.error('❌ Erro ao carregar CMVs:', error);
      // Não mostrar toast para evitar spam durante a digitação
      if (!cmvFilters.search) {
        toast.error('Erro ao carregar CMVs');
      }
    } finally {
      setIsLoadingCMVs(false);
    }
  };

  const handleOpenMotoristaModal = async (os: OrdemServico) => {
    // Não permitir atribuir motorista a OSs do Sisfuner que ainda não foram importadas
    if (os.isSisfuner) {
      toast.error('Esta OS ainda não foi importada. Clique em "Iniciar Monitoramento" para importá-la.');
      return;
    }
    try {
      // Sempre buscar fases atualizadas da OS para garantir que temos os dados mais recentes
        const fasesResponse = await api.get(`/os/${os.id}/fases`);
        const fases = fasesResponse.data.fases || [];
      const osAtualizada = { ...os, fases };
      
      // Buscar motoristas disponíveis
      await fetchMotoristas();
      
      // Resetar filtros e buscar CMVs com data padrão (hoje)
      setCmvFilters({
        data: new Date().toISOString().split('T')[0],
        turno: '',
        search: ''
      });
      setSelectedCMV(null);
      setShowCMVList(false);
      await fetchCMVs();
      
      // Abrir modal com OS atualizada
      setSelectedOS(osAtualizada);
      setSelectedFase('');
      setSelectedMotorista(0);
      setSelectedCMV(null);
      setShowCMVList(false);
      setShowMotoristaAssignPrompt(false);
      setLastAssignedFaseNome(null);
    } catch (error: any) {
      console.error('Erro ao abrir modal de motorista:', error);
      toast.error('Erro ao carregar dados para atribuição de motorista');
    }
  };

  const refreshMotoristaModalOs = async (osId: number) => {
    const fasesResponse = await api.get(`/os/${osId}/fases`);
    const fases = fasesResponse.data.fases || [];
    setSelectedOS((prev) => (prev ? { ...prev, fases } : prev));
    return fases;
  };

  const resetMotoristaFormSelection = () => {
    setSelectedFase('');
    setSelectedMotorista(0);
    setSelectedCMV(null);
    setShowCMVList(false);
    setCmvFilters((prev) => ({ ...prev, search: '' }));
  };

  const handleAtribuirMotorista = async (osId: number, faseNome: string, motoristaId: number) => {
    try {
      setIsAssigningMotorista(true);
      console.log(`🔍 Atribuindo motorista ${motoristaId} à fase ${faseNome} da OS ${osId}`);
      
      // Encontrar a fase específica para obter o ID
      const fase = selectedOS?.fases?.find(f => f.nome_fase === faseNome);
      if (!fase || !fase.id) {
        toast.error('Fase não encontrada. Recarregue a página e tente novamente.');
        return;
      }

      // Buscar CMV selecionado ou último CMV do motorista
      let veiculoId: number | undefined = undefined;
      let cmvId: number | undefined = undefined;

      if (selectedCMV) {
        const cmv = cmvs.find(c => c.id === selectedCMV);
        if (cmv) {
          cmvId = cmv.id;
          veiculoId = cmv.veiculo_id;
        }
      }
      
      // Chamar o endpoint para atribuir motorista
      const response = await api.put(`/os/${osId}/fases/${fase.id}/atribuir-motorista`, {
        motorista_id: motoristaId,
        veiculo_id: veiculoId,
        cmv_id: cmvId
      });
      
      console.log('✅ Resposta da API:', response.data);

      await refreshMotoristaModalOs(osId);
      resetMotoristaFormSelection();
      fetchOrdens({ silent: true });

      setLastAssignedFaseNome(faseNome);
      setShowMotoristaAssignPrompt(true);
    } catch (error: any) {
      console.error('❌ Erro ao atribuir motorista:', error);
      
      if (error.response?.status === 400) {
        toast.error(`Erro de validação: ${error.response.data.error}`);
      } else if (error.response?.status === 404) {
        toast.error('OS ou fase não encontrada');
      } else {
        toast.error('Erro ao atribuir motorista à fase');
      }
    } finally {
      setIsAssigningMotorista(false);
    }
  };

  const handleRemoverMotorista = async (osId: number, faseId: number, faseNome: string) => {
    try {
      if (!window.confirm(`Tem certeza que deseja remover o motorista da fase "${faseNome}"?`)) {
        return;
      }

      console.log(`🔍 Removendo motorista da fase ${faseNome} da OS ${osId}`);
      
      // Chamar o endpoint para remover motorista
      const response = await api.delete(`/os/${osId}/fases/${faseId}/remover-motorista`);
      
      console.log('✅ Resposta da API:', response.data);
      
      toast.success(`Motorista removido da fase ${faseNome} com sucesso!`);
      
      // Se o modal estiver aberto, atualizar os dados da OS selecionada primeiro
      if (selectedOS) {
        await refreshMotoristaModalOs(osId);
        if (selectedFase === faseNome) {
          resetMotoristaFormSelection();
        }
      }
      
      // Recarregar lista para mostrar as mudanças
      fetchOrdens({ silent: true });
    } catch (error: any) {
      console.error('❌ Erro ao remover motorista:', error);
      
      if (error.response?.status === 400) {
        toast.error(`Erro: ${error.response.data.error}`);
      } else if (error.response?.status === 404) {
        toast.error('OS ou fase não encontrada');
      } else {
        toast.error('Erro ao remover motorista da fase');
      }
    }
  };

  const handleExcluirFase = async (fase: FaseOS, index: number) => {
    if (!editData?.id) return;

    if (!window.confirm(`Tem certeza que deseja excluir a fase "${fase.nome_fase}"?`)) {
      return;
    }

    try {
      if (fase.id && fase.id > 0) {
        const response = await api.delete(`/os/${editData.id}/fases/${fase.id}`);
        setEditData({ ...editData, fases: response.data.fases || [] });
      } else {
        const novasFases = reordenarFasesLocal(editData.fases.filter((_, i) => i !== index));
        setEditData({ ...editData, fases: novasFases });
      }

      toast.success(`Fase "${fase.nome_fase}" excluída com sucesso!`);
      fetchOrdens({ silent: true });
    } catch (error: any) {
      console.error('❌ Erro ao excluir fase:', error);

      if (error.response?.status === 400) {
        toast.error(error.response.data.error || 'Não foi possível excluir a fase');
      } else if (error.response?.status === 404) {
        toast.error('OS ou fase não encontrada');
      } else {
        toast.error('Erro ao excluir fase');
      }
    }
  };

  const handleCreateOS = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Log dos dados sendo enviados
    console.log('🔍 Dados sendo enviados para API:', formData);
    
    try {
      if (selectedOS) {
        // Adicionar fases a uma OS existente
        const fasesComStatus = formData.fases.map(fase => ({
          nome_fase: fase.nome_fase,
          ordem: fase.ordem,
          endereco_destino: fase.endereco_destino,
          status_fase: 'Pendente'
        }));
        await api.post(`/os/${selectedOS.id}/fases`, { fases: fasesComStatus });
        toast.success('Fases adicionadas com sucesso à OS!');
        setShowCreateForm(false);
        setSelectedOS(null);
        setFormData({
          nome_falecido: '',
          numero_declaracao_obito: '',
          numero_nota_contratacao: '',
          observacoes: '',
          fases: [],
          isSisfuner: false
        });
      } else if (formData.isSisfuner && formData.numeroOrdemSisfuner) {
        // Criar OS do Sisfuner (importação acontece automaticamente pelo numero_nota_contratacao)
        toast.loading('Criando OS do Sisfuner...', { id: 'criando' });
        
        // Preparar dados para criação
        const fasesParaCriar = formData.fases
          .filter(fase => (fase.endereco_destino && fase.endereco_destino.trim()) || (fase.nome_local && fase.nome_local.trim())) // Apenas fases com dados
          .map(fase => ({
            nome_fase: fase.nome_fase,
            ordem: fase.ordem,
            endereco_destino: fase.endereco_destino && fase.endereco_destino.trim() ? fase.endereco_destino : null,
            nome_local: fase.nome_local && fase.nome_local.trim() ? fase.nome_local : null,
            data: fase.data && fase.data.trim() ? fase.data : null,
            horario: fase.horario && fase.horario.trim() ? fase.horario : null
          }));

        const osData = {
          nome_falecido: formData.nome_falecido,
          numero_declaracao_obito: formData.numero_declaracao_obito || null,
          numero_nota_contratacao: formData.numero_nota_contratacao,
          observacoes: formData.observacoes,
          fases: fasesParaCriar,
          velorio_sepultamento_mesmo_local: Boolean(formData.velorio_sepultamento_mesmo_local),
          origem: 'sisfuner',
          contratante_nome: formData.contratante_nome || null,
          contratante_telefone1: formData.contratante_telefone1 || null,
          contratante_telefone2: formData.contratante_telefone2 || null
        };

        await api.post('/os', osData);
        toast.success('OS do Sisfuner criada com sucesso!', { id: 'criando' });
        setShowCreateForm(false);
        setFormData({
          nome_falecido: '',
          numero_declaracao_obito: '',
          numero_nota_contratacao: '',
          observacoes: '',
          fases: [],
          isSisfuner: false
        });
      } else {
        // Criar nova OS normal
        const fasesParaEnviar = formData.fases
          .filter(fase => (fase.endereco_destino && fase.endereco_destino.trim()) || (fase.nome_local && fase.nome_local.trim())) // Apenas fases com dados
          .map(fase => ({
            nome_fase: fase.nome_fase,
            ordem: fase.ordem,
            endereco_destino: fase.endereco_destino && fase.endereco_destino.trim() ? fase.endereco_destino : null,
            nome_local: fase.nome_local && fase.nome_local.trim() ? fase.nome_local : null,
            data: fase.data && fase.data.trim() ? fase.data : null,
            horario: fase.horario && fase.horario.trim() ? fase.horario : null
          }));

        const osComStatus = {
          nome_falecido: formData.nome_falecido,
          numero_declaracao_obito: formData.numero_declaracao_obito || null,
          numero_nota_contratacao: formData.numero_nota_contratacao,
          observacoes: formData.observacoes,
          fases: fasesParaEnviar,
          velorio_sepultamento_mesmo_local: Boolean(formData.velorio_sepultamento_mesmo_local)
        };
        
        await api.post('/os', osComStatus);
        toast.success('Ordem de serviço criada com sucesso!');
        setShowCreateForm(false);
        setFormData({
          nome_falecido: '',
          numero_declaracao_obito: '',
          numero_nota_contratacao: '',
          observacoes: '',
          fases: [],
          isSisfuner: false
        });
      }
      
      fetchOrdens(); // Recarregar lista
      voltarParaKanban();
    } catch (error: any) {
      console.error('❌ Erro completo:', error);
      console.error('❌ Resposta da API:', error.response?.data);
      if (formData.isSisfuner) {
        toast.error('Erro ao criar OS do Sisfuner', { id: 'criando' });
      } else {
        toast.error(selectedOS ? 'Erro ao adicionar fases à OS' : 'Erro ao criar ordem de serviço');
      }
      console.error('Erro:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pendente': return 'bg-yellow-100 text-yellow-800';
      case 'Em Andamento': return 'bg-blue-100 text-blue-800';
      case 'Concluída': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Função para verificar se uma fase está próxima (1 hora antes) ou atrasada
  const getFaseAlertaStatus = (fase: FaseOS): 'proxima' | 'atrasada' | null => {
    // Apenas verificar fases pendentes (não iniciadas)
    if (fase.status_fase !== 'Pendente') {
      return null;
    }

    // Verificar se tem data e horário
    if (!fase.data_fase || !fase.horario) {
      return null;
    }

    try {
      // Converter horário para formato válido (pode vir como HH:MM ou HHMM)
      let horarioFormatado = fase.horario.trim();
      if (!horarioFormatado.includes(':')) {
        // Se está no formato HHMM, converter para HH:MM
        if (horarioFormatado.length === 4) {
          horarioFormatado = `${horarioFormatado.substring(0, 2)}:${horarioFormatado.substring(2, 4)}`;
        } else {
          return null;
        }
      }

      // Criar objeto Date combinando data_fase e horario
      const dataHoraFase = new Date(`${fase.data_fase}T${horarioFormatado}:00`);
      
      // Verificar se a data é válida
      if (isNaN(dataHoraFase.getTime())) {
        return null;
      }

      // Calcular diferença em milissegundos
      const agora = new Date();
      const diffMs = dataHoraFase.getTime() - agora.getTime();
      const diffMinutos = diffMs / (1000 * 60);
      const diffHoras = diffMinutos / 60;

      // Se já passou do horário (atrasada)
      if (diffMs < 0) {
        return 'atrasada';
      }

      // Se está a 1 hora ou menos (próxima)
      if (diffHoras <= 1 && diffHoras >= 0) {
        return 'proxima';
      }

      return null;
    } catch (error) {
      console.error('Erro ao calcular status de alerta da fase:', error);
      return null;
    }
  };

  // Função para obter informações de alerta de uma OS
  const getOSAlertas = (os: OrdemServico): { 
    temAlerta: boolean; 
    alertas: Array<{ fase: FaseOS; status: 'proxima' | 'atrasada' }>; 
    temAtrasada: boolean;
  } => {
    if (!os.fases || os.fases.length === 0) {
      return { temAlerta: false, alertas: [], temAtrasada: false };
    }

    const alertas = os.fases
      .map(fase => {
        const status = getFaseAlertaStatus(fase);
        return status ? { fase, status } : null;
      })
      .filter((alert): alert is { fase: FaseOS; status: 'proxima' | 'atrasada' } => alert !== null);

    const temAtrasada = alertas.some(a => a.status === 'atrasada');

    return {
      temAlerta: alertas.length > 0,
      alertas,
      temAtrasada
    };
  };

  // Função para formatar tempo restante
  const formatarTempoRestante = (fase: FaseOS): string => {
    if (!fase.data_fase || !fase.horario) {
      return '';
    }

    try {
      let horarioFormatado = fase.horario.trim();
      if (!horarioFormatado.includes(':')) {
        if (horarioFormatado.length === 4) {
          horarioFormatado = `${horarioFormatado.substring(0, 2)}:${horarioFormatado.substring(2, 4)}`;
        } else {
          return '';
        }
      }

      const dataHoraFase = new Date(`${fase.data_fase}T${horarioFormatado}:00`);
      if (isNaN(dataHoraFase.getTime())) {
        return '';
      }

      const agora = new Date();
      const diffMs = dataHoraFase.getTime() - agora.getTime();
      
      if (diffMs < 0) {
        const minutosAtraso = Math.abs(Math.floor(diffMs / (1000 * 60)));
        const horasAtraso = Math.floor(minutosAtraso / 60);
        if (horasAtraso > 0) {
          return `${horasAtraso}h ${minutosAtraso % 60}min atrasado`;
        }
        return `${minutosAtraso}min atrasado`;
      }

      const minutosRestantes = Math.floor(diffMs / (1000 * 60));
      const horasRestantes = Math.floor(minutosRestantes / 60);
      
      if (horasRestantes > 0) {
        return `em ${horasRestantes}h ${minutosRestantes % 60}min`;
      }
      return `em ${minutosRestantes}min`;
    } catch (error) {
      return '';
    }
  };

  const handleIniciarMonitoramento = async (os: OrdemServico) => {
    if (!os._DADOSOS_NumOrdem) {
      toast.error('Número da ordem do Sisfuner não encontrado');
      return;
    }

    try {
      toast.loading('Carregando dados do Sisfuner...', { id: 'carregando' });
      
      // Buscar dados completos da OS no Sisfuner
      const response = await api.get(`/sisfuner/os/${os._DADOSOS_NumOrdem}`);
      const osSisfuner = response.data.os;

      // Função auxiliar para montar endereço completo
      const montarEndereco = (logradouro: string, numero: string, complemento: string, bairro: string, cidade: string, uf: string, cep: string) => {
        const partes = [];
        if (logradouro) partes.push(logradouro);
        if (numero) partes.push(`nº ${numero}`);
        if (complemento) partes.push(complemento);
        if (bairro) partes.push(bairro);
        if (cidade) partes.push(cidade);
        if (uf) partes.push(uf);
        if (cep) partes.push(`CEP: ${cep}`);
        return partes.join(', ') || '';
      };

      // Função auxiliar para formatar data evitando problemas de timezone
      const formatarDataSegura = (data: any): string => {
        if (!data) return '';
        
        // Se já for string no formato YYYY-MM-DD, retornar diretamente
        if (typeof data === 'string') {
          const datePart = data.split('T')[0].split(' ')[0];
          if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return datePart;
          }
        }
        
        // Se for objeto Date ou string em outro formato, extrair componentes locais
        try {
          const date = data instanceof Date ? data : new Date(data);
          if (!isNaN(date.getTime())) {
            // Usar métodos locais para evitar problemas de timezone
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
        } catch {
          // Se falhar, tentar extrair data de string
          if (typeof data === 'string') {
            const match = data.match(/(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
              return match[0];
            }
          }
        }
        
        return '';
      };

      // Função para converter formato HHMM (ex: 2000) para HH:MM (ex: 20:00)
      const converterHoraHHMM = (horaHHMM: string | null | undefined): string => {
        if (!horaHHMM) return '';
        
        // Remover espaços e garantir que seja string
        const horaStr = String(horaHHMM).trim();
        
        // Se já estiver no formato HH:MM, retornar como está
        if (horaStr.includes(':')) {
          return horaStr;
        }
        
        // Se tiver menos de 4 dígitos, preencher com zeros à esquerda
        const horaPadded = horaStr.padStart(4, '0');
        
        // Validar que tem 4 dígitos
        if (horaPadded.length !== 4 || !/^\d{4}$/.test(horaPadded)) {
          return '';
        }
        
        // Extrair horas e minutos
        const horas = horaPadded.substring(0, 2);
        const minutos = horaPadded.substring(2, 4);
        
        // Validar que horas estão entre 00-23 e minutos entre 00-59
        const horasNum = parseInt(horas, 10);
        const minutosNum = parseInt(minutos, 10);
        
        if (horasNum < 0 || horasNum > 23 || minutosNum < 0 || minutosNum > 59) {
          return '';
        }
        
        // Retornar no formato HH:MM
        return `${horas}:${minutos}`;
      };

      // Preparar fases com dados do Sisfuner
      const fases: FaseForm[] = [];

      // Fase 1: Remoção
      if (osSisfuner._REMOCAO_RemocaoLogradouro || osSisfuner._REMOCAO_RemocaoNome) {
        const enderecoRemocao = montarEndereco(
          osSisfuner._REMOCAO_RemocaoLogradouro || osSisfuner._REMOCAO_RemocaoNome || '',
          osSisfuner._REMOCAO_RemocaoNumero || '',
          osSisfuner._REMOCAO_RemocaoComplemento || '',
          osSisfuner._REMOCAO_RemocaoBairro || '',
          osSisfuner._REMOCAO_RemocaoCidade || '',
          osSisfuner._REMOCAO_RemocaoUF || '',
          osSisfuner._REMOCAO_RemocaoCEP || ''
        );
        fases.push({
          nome_fase: 'Remoção',
          ordem: 1,
          endereco_destino: enderecoRemocao,
          nome_local: osSisfuner._REMOCAO_RemocaoNome || '',
          data: formatarDataSegura(osSisfuner._REMOCAO_RemocaoData),
          horario: converterHoraHHMM(osSisfuner._REMOCAO_RemocaoHora)
        });
      }

      // Fase 2: Clínica/Laboratório
      if (osSisfuner._REMOCAO_LaboratorioLogradouro || osSisfuner._REMOCAO_LaboratorioNome) {
        const enderecoLaboratorio = montarEndereco(
          osSisfuner._REMOCAO_LaboratorioLogradouro || osSisfuner._REMOCAO_LaboratorioNome || '',
          osSisfuner._REMOCAO_LaboratorioNumero || '',
          osSisfuner._REMOCAO_LaboratorioComplemento || '',
          osSisfuner._REMOCAO_LaboratorioBairro || '',
          osSisfuner._REMOCAO_LaboratorioCidade || '',
          osSisfuner._REMOCAO_LaboratorioUF || '',
          osSisfuner._REMOCAO_LaboratorioCEP || ''
        );
        fases.push({
          nome_fase: 'Clínica',
          ordem: 2,
          endereco_destino: enderecoLaboratorio,
          nome_local: osSisfuner._REMOCAO_LaboratorioNome || '',
          data: '',
          horario: ''
        });
      }

      // Fase 3: Velório
      if (osSisfuner._REMOCAO_VelorioLogradouro) {
        const enderecoVelorio = montarEndereco(
          osSisfuner._REMOCAO_VelorioLogradouro || '',
          osSisfuner._REMOCAO_VelorioNumero || '',
          osSisfuner._REMOCAO_VelorioComplemento || '',
          osSisfuner._REMOCAO_VelorioBairro || '',
          osSisfuner._REMOCAO_VelorioCidade || '',
          osSisfuner._REMOCAO_VelorioUF || '',
          osSisfuner._REMOCAO_VelorioCEP || ''
        );
        fases.push({
          nome_fase: 'Velório',
          ordem: 3,
          endereco_destino: enderecoVelorio,
          nome_local: '',
          data: formatarDataSegura(osSisfuner._REMOCAO_VelorioData),
          horario: converterHoraHHMM(osSisfuner._REMOCAO_VelorioHora)
        });
      }

      // Fase 4: Sepultamento
      if (osSisfuner._SEPULTAMENTO_Logradouro || osSisfuner._SEPULTAMENTO_Nome) {
        const enderecoSepultamento = montarEndereco(
          osSisfuner._SEPULTAMENTO_Logradouro || osSisfuner._SEPULTAMENTO_Nome || '',
          osSisfuner._Sepultamento_Numero || '',
          osSisfuner._SEPULTAMENTO_Complemento || '',
          osSisfuner._SEPULTAMENTO_Bairro || '',
          osSisfuner._SEPULTAMENTO_Cidade || '',
          osSisfuner._SEPULTAMENTO_UF || '',
          osSisfuner._SEPULTAMENTO_Cep || ''
        );
        fases.push({
          nome_fase: 'Sepultamento',
          ordem: 4,
          endereco_destino: enderecoSepultamento,
          nome_local: osSisfuner._SEPULTAMENTO_Nome || '',
          data: formatarDataSegura(osSisfuner._SEPULTAMENTO_Data),
          horario: converterHoraHHMM(osSisfuner._SEPULTAMENTO_Hora)
        });
      }

      // Preparar observações
      const observacoes = [
        `Contratante: ${osSisfuner._CONTRATANTE_Contratante || 'Não informado'}`,
        `Telefone 1: ${osSisfuner._CONTRATANTE_Fone1 || 'Não informado'}`,
        `Telefone 2: ${osSisfuner._CONTRATANTE_Fone2 || 'Não informado'}`,
        `Data Falecimento: ${osSisfuner._FALECIDO_DataFalecimento || 'Não informado'} ${osSisfuner._FALECIDO_HoraFalecimento || ''}`.trim()
      ].join('\n');

      // Preencher formulário com dados do Sisfuner
      setFormData({
        nome_falecido: osSisfuner._FALECIDO_Nome || '',
        numero_declaracao_obito: '',
        numero_nota_contratacao: osSisfuner._DADOSOS_NumOrdem?.toString() || '',
        observacoes: observacoes,
        fases: fases,
        isSisfuner: true,
        numeroOrdemSisfuner: osSisfuner._DADOSOS_NumOrdem?.toString(),
        contratante_nome: osSisfuner._CONTRATANTE_Contratante?.toString().trim() || '',
        contratante_telefone1: osSisfuner._CONTRATANTE_Fone1?.toString().trim() || '',
        contratante_telefone2: osSisfuner._CONTRATANTE_Fone2?.toString().trim() || ''
      });

      // Abrir formulário
      setShowCreateForm(true);
      setSelectedOS(null);
      shouldScrollToCreateFormRef.current = true;
      
      toast.success('Dados do Sisfuner carregados! Preencha os campos e clique em "Criar OS" para importar.', { id: 'carregando' });
      
    } catch (error: any) {
      console.error('❌ Erro ao carregar dados do Sisfuner:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao carregar dados do Sisfuner';
      toast.error(errorMessage, { id: 'carregando' });
      voltarParaKanban();
    }
  };

  const handleViewOS = async (os: OrdemServico) => {
    // Não permitir visualizar OSs do Sisfuner que ainda não foram importadas
    if (os.isSisfuner) {
      toast.error('Esta OS ainda não foi importada. Clique em "Iniciar Monitoramento" para importá-la.');
      return;
    }

    console.log('🔍 OS selecionada para visualização:', os);

    if (os.id) {
      void carregarNotificacoesWhatsApp(os.id);
    }
    
    try {
      // Buscar fases da OS separadamente
      const fasesResponse = await api.get(`/os/${os.id}/fases`);
      const fases = fasesResponse.data.fases || [];
      
      console.log('🔍 Fases buscadas da API:', fases);
      
      // Criar OS com fases
      const osComFases = { ...os, fases };
      setSelectedOS(osComFases);
      setShowViewModal(true);
    } catch (error: any) {
      console.error('❌ Erro ao buscar fases:', error);
      toast.error('Erro ao carregar fases da OS');
      // Mostrar OS sem fases
      setSelectedOS(os);
      setShowViewModal(true);
    }
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedOS(null);
    setNotificacoesWhatsApp([]);
    voltarParaKanban();
  };

  const carregarNotificacoesWhatsApp = async (osId: number) => {
    setLoadingWhatsApp(true);
    try {
      const response = await api.get(`/os/${osId}/whatsapp`);
      setNotificacoesWhatsApp(response.data.notificacoes || []);
    } catch {
      setNotificacoesWhatsApp([]);
    } finally {
      setLoadingWhatsApp(false);
    }
  };

  const handleReenviarWhatsApp = async (os: OrdemServico) => {
    if (!os.id || reenviandoWhatsApp) return;
    if (!os.contratante_telefone1) {
      toast.error('Informe o Telefone 1 do contratante (em Editar) antes de enviar.');
      return;
    }
    const confirmado = window.confirm(
      `Enviar o WhatsApp de acompanhamento para ${os.contratante_nome || 'o contratante'} (${os.contratante_telefone1})?`
    );
    if (!confirmado) return;

    setReenviandoWhatsApp(true);
    try {
      await api.post(`/os/${os.id}/whatsapp/acompanhamento`, undefined, { timeout: 30000 });
      toast.success('WhatsApp enviado com sucesso');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Não foi possível enviar o WhatsApp');
    } finally {
      setReenviandoWhatsApp(false);
      await carregarNotificacoesWhatsApp(os.id);
    }
  };

  const handleEditOS = async (os: OrdemServico) => {
    // Não permitir editar OSs do Sisfuner que ainda não foram importadas
    if (os.isSisfuner) {
      toast.error('Esta OS ainda não foi importada. Clique em "Iniciar Monitoramento" para importá-la.');
      return;
    }

    console.log('🔍 OS selecionada para edição:', os);
    
    try {
      // Buscar fases da OS separadamente
      const fasesResponse = await api.get(`/os/${os.id}/fases`);
      let fases = fasesResponse.data.fases || [];
      
      console.log('🔍 Fases buscadas da API para edição:', fases);
      
      // Se não há fases, criar as 4 fases padrão
      if (fases.length === 0) {
        fases = [
          { id: null, nome_fase: 'Remoção', ordem: 1, endereco_destino: '', status_fase: 'Pendente' },
          { id: null, nome_fase: 'Clínica', ordem: 2, endereco_destino: '', status_fase: 'Pendente' },
          { id: null, nome_fase: 'Velório', ordem: 3, endereco_destino: '', status_fase: 'Pendente' },
          { id: null, nome_fase: 'Sepultamento', ordem: 4, endereco_destino: '', status_fase: 'Pendente' }
        ];
        console.log('🔍 Fases padrão criadas para edição:', fases);
      }
      
      // Criar OS com fases para edição
      const osComFases = { ...os, fases };
      setEditData(osComFases);
      setShowEditModal(true);
    } catch (error: any) {
      console.error('❌ Erro ao buscar fases para edição:', error);
      toast.error('Erro ao carregar fases da OS');
      
      // Em caso de erro, criar OS com fases padrão
      const fasesPadrao = [
        { id: null, nome_fase: 'Remoção', ordem: 1, endereco_destino: '', status_fase: 'Pendente' },
        { id: null, nome_fase: 'Clínica', ordem: 2, endereco_destino: '', status_fase: 'Pendente' },
        { id: null, nome_fase: 'Velório', ordem: 3, endereco_destino: '', status_fase: 'Pendente' },
        { id: null, nome_fase: 'Sepultamento', ordem: 4, endereco_destino: '', status_fase: 'Pendente' }
      ];
      const osComFases = { ...os, fases: fasesPadrao };
      setEditData(osComFases);
      setShowEditModal(true);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditData(null);
    voltarParaKanban();
  };

  // Ações vindas do Kanban (Ver / Editar / Motorista / Iniciar Monitoramento)
  useEffect(() => {
    const state = location.state as OsKanbanNavigationState | null;
    if (!state?.fromKanban) {
      kanbanActionHandledRef.current = false;
      return;
    }
    if (kanbanActionHandledRef.current || loading) return;

    const executarAcaoKanban = async () => {
      returnToKanbanRef.current = Boolean(state.returnToKanban ?? state.fromKanban);

      if (state.action === 'monitor' && state.numOrdemSisfuner) {
        const os = safeOrdens.find(
          (o) =>
            o.isSisfuner &&
            String(o._DADOSOS_NumOrdem) === String(state.numOrdemSisfuner)
        );
        if (!os) {
          voltarParaKanban();
          return;
        }
        kanbanActionHandledRef.current = true;
        setActiveTab('pendentes');
        await handleIniciarMonitoramento(os);
        navigate(location.pathname, { replace: true, state: null });
        return;
      }

      if (!state.osId) return;
      const os = safeOrdens.find((o) => !o.isSisfuner && o.id === state.osId);
      if (!os) {
        voltarParaKanban();
        return;
      }

      kanbanActionHandledRef.current = true;
      setActiveTab('monitoramento');

      if (state.action === 'view') {
        await handleViewOS(os);
      } else if (state.action === 'edit') {
        await handleEditOS(os);
      } else if (state.action === 'assign') {
        await handleOpenMotoristaModal(os);
      }

      navigate(location.pathname, { replace: true, state: null });
    };

    if (safeOrdens.length > 0) {
      executarAcaoKanban();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, loading, ordens.length]);

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editData) return;
    
    try {
      console.log('🔍 === INICIANDO EDIÇÃO ===');
      console.log('🔍 editData completo:', editData);
      console.log('🔍 editData.fases:', editData.fases);
      console.log('🔍 editData.fases.length:', editData.fases?.length);
      
      // Separar fases existentes e novas
      const fasesExistentes = editData.fases.filter(fase => fase.id && fase.id > 0);
      const fasesNovas = editData.fases.filter(fase => !fase.id || fase.id <= 0);
      
      console.log('🔍 Fases existentes:', fasesExistentes);
      console.log('🔍 Fases novas:', fasesNovas);
      console.log('🔍 Fases novas length:', fasesNovas.length);
      
      // Preparar dados para envio (dados básicos da OS)
      const updateData = {
        nome_falecido: editData.nome_falecido,
        numero_declaracao_obito: editData.numero_declaracao_obito,
        numero_nota_contratacao: editData.numero_nota_contratacao,
        observacoes: editData.observacoes,
        velorio_sepultamento_mesmo_local: Boolean(editData.velorio_sepultamento_mesmo_local),
        contratante_nome: editData.contratante_nome ?? '',
        contratante_telefone1: editData.contratante_telefone1 ?? '',
        contratante_telefone2: editData.contratante_telefone2 ?? ''
      };

      console.log('🔍 Dados de edição sendo enviados:', updateData);
      
      // Atualizar dados básicos da OS
      console.log('🔍 Chamando PUT /os/${editData.id}');
      await api.put(`/os/${editData.id}`, updateData);
      console.log('✅ Dados básicos atualizados com sucesso');
      
      // Se há fases novas, adicioná-las
      if (fasesNovas.length > 0) {
        console.log('🔍 Adicionando fases novas:', fasesNovas);
        console.log('🔍 Chamando POST /os/${editData.id}/fases');
        
                 const fasesParaEnviar = fasesNovas.map(fase => ({
           nome_fase: fase.nome_fase,
           endereco_destino: fase.endereco_destino,
           ordem: fase.ordem,
           status_fase: fase.status_fase || 'Pendente',
           nome_local: fase.nome_local || null,
           data: fase.data_fase ? (typeof fase.data_fase === 'string' && fase.data_fase.match(/^\d{4}-\d{2}-\d{2}/) 
             ? fase.data_fase.split('T')[0] 
             : new Date(fase.data_fase).toISOString()) : null,
           horario: fase.horario || null
         }));
        
        console.log('🔍 Fases para enviar:', fasesParaEnviar);
        
        const fasesResponse = await api.post(`/os/${editData.id}/fases`, { 
          fases: fasesParaEnviar
        });
        
        console.log('✅ Fases criadas com sucesso:', fasesResponse.data);
      } else {
        console.log('⚠️ Nenhuma fase nova para adicionar');
      }
      
      // Se há fases existentes, atualizá-las
      if (fasesExistentes.length > 0) {
        console.log('🔍 Fases existentes para atualizar:', fasesExistentes);
        
        for (const fase of fasesExistentes) {
          const updateData: any = {};
          
          // Sempre enviar endereco_destino e status_fase se existirem
          if (fase.endereco_destino !== undefined) {
            updateData.endereco_destino = fase.endereco_destino || '';
          }
          if (fase.status_fase !== undefined) {
            updateData.status_fase = fase.status_fase || 'Pendente';
          }
          
          if (fase.nome_local !== undefined) {
            updateData.nome_local = fase.nome_local || null;
          }
          
          if (fase.data_fase !== undefined) {
            // Converter para formato YYYY-MM-DD
            if (fase.data_fase) {
              if (typeof fase.data_fase === 'string' && fase.data_fase.match(/^\d{4}-\d{2}-\d{2}/)) {
                // Já está no formato correto
                updateData.data_fase = fase.data_fase.split('T')[0];
              } else {
                // Tentar converter para Date e depois para YYYY-MM-DD
                const dateObj = new Date(fase.data_fase);
                if (!isNaN(dateObj.getTime())) {
                  const year = dateObj.getFullYear();
                  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                  const day = String(dateObj.getDate()).padStart(2, '0');
                  updateData.data_fase = `${year}-${month}-${day}`;
                } else {
                  updateData.data_fase = null;
                }
              }
            } else {
              updateData.data_fase = null;
            }
          }
          
          if (fase.horario !== undefined) {
            // Garantir formato correto do horário (HH:MM)
            if (fase.horario && typeof fase.horario === 'string') {
              let horarioFormatado = fase.horario.trim();
              // Se não tiver ':', tentar formatar (ex: "1730" -> "17:30")
              if (!horarioFormatado.includes(':')) {
                if (horarioFormatado.length === 4 && /^\d{4}$/.test(horarioFormatado)) {
                  horarioFormatado = `${horarioFormatado.substring(0, 2)}:${horarioFormatado.substring(2, 4)}`;
                }
              }
              updateData.horario = horarioFormatado || null;
            } else {
              updateData.horario = null;
            }
          }
          
          console.log(`🔍 Atualizando fase ${fase.id}:`, updateData);
          await api.put(`/os/${editData.id}/fases/${fase.id}`, updateData);
        }
        
        console.log('✅ Fases existentes atualizadas com sucesso');
      }
      
      toast.success('Ordem de serviço atualizada com sucesso!');
      closeEditModal();
      fetchOrdens(); // Recarregar lista
    } catch (error: any) {
      console.error('❌ Erro ao editar OS:', error);
      console.error('❌ Resposta da API:', error.response?.data);
      console.error('❌ Status da resposta:', error.response?.status);
      toast.error('Erro ao atualizar ordem de serviço');
    }
  };

  if (loading) {
    console.log('🔄 Mostrando loading...');
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  console.log('✅ Renderizando conteúdo principal...');
  console.log('🔍 Debug - ordens:', ordens);
  console.log('🔍 Debug - typeof ordens:', typeof ordens);
  console.log('🔍 Debug - Array.isArray(ordens):', Array.isArray(ordens));
  console.log('🔍 Debug - safeOrdens:', safeOrdens);
  return (
    <div className="w-full">
      <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Ordens de Serviço</h1>
          <button
            onClick={() => setShowCreateForm(true)}
            className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Nova OS
          </button>
        </div>

        {/* Formulário de criação */}
        {showCreateForm && (
          <div ref={createFormRef} className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6 scroll-mt-4">
           <h2 className="text-lg font-semibold mb-4">
             {selectedOS ? 'Adicionar Fases à OS' : 'Nova Ordem de Serviço'}
           </h2>
           <form onSubmit={handleCreateOS}>
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome do Falecido
                </label>
                <input
                  type="text"
                  value={formData.nome_falecido}
                  onChange={(e) => setFormData({...formData, nome_falecido: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={!!selectedOS} // Desabilitar se for adição de fases
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nº Declaração de Óbito
                </label>
                  <input
                  type="text"
                  value={formData.numero_declaracao_obito}
                  onChange={(e) => setFormData({...formData, numero_declaracao_obito: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={!formData.isSisfuner}
                  disabled={!!selectedOS || formData.isSisfuner} // Desabilitar se for adição de fases ou OS do Sisfuner
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nº Nota de Contratação
                </label>
                <input
                  type="text"
                  value={formData.numero_nota_contratacao}
                  onChange={(e) => setFormData({...formData, numero_nota_contratacao: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={!!selectedOS || formData.isSisfuner} // Desabilitar se for adição de fases ou OS do Sisfuner
                />
              </div>
            </div>

            {formData.isSisfuner && !selectedOS && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-medium text-green-900 mb-1">WhatsApp de acompanhamento da família</p>
                <p className="text-xs text-green-800 mb-3">
                  Ao criar a OS, o contratante receberá no Telefone 1 o link do portal Family.
                  Confira se o Telefone 1 é um celular: a família entra no portal com a Nota de Contratação e os 4 últimos dígitos desse número.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contratante</label>
                    <input
                      type="text"
                      value={formData.contratante_nome || ''}
                      onChange={(e) => setFormData({ ...formData, contratante_nome: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      maxLength={150}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefone 1 (WhatsApp)</label>
                    <input
                      type="tel"
                      value={formData.contratante_telefone1 || ''}
                      onChange={(e) => setFormData({ ...formData, contratante_telefone1: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="(11) 91234-5678"
                      maxLength={30}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefone 2</label>
                    <input
                      type="tel"
                      value={formData.contratante_telefone2 || ''}
                      onChange={(e) => setFormData({ ...formData, contratante_telefone2: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      maxLength={30}
                    />
                  </div>
                </div>
              </div>
            )}
            
            <div className="mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observações
                </label>
                <input
                  type="text"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={!!selectedOS} // Desabilitar se for adição de fases
                />
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Fases da OS
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const novasFases = [...formData.fases];
                    const ordensExistentes = novasFases.map(f => f.ordem);
                    const proximaOrdem = ordensExistentes.length > 0 ? Math.max(...ordensExistentes) + 1 : 1;
                    novasFases.push({
                      nome_fase: 'Nova Fase',
                      ordem: proximaOrdem,
                      endereco_destino: '',
                      nome_local: '',
                      data: '',
                      horario: ''
                    });
                    setFormData({...formData, fases: novasFases});
                  }}
                  className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors"
                >
                  + Adicionar Fase
                </button>
              </div>
              {formData.fases.length === 0 && (
                <p className="text-sm text-gray-500 mb-3 italic">
                  Nenhuma fase adicionada. Clique em "Adicionar Fase" para começar.
                </p>
              )}
              {formData.fases.map((fase, index) => (
                <React.Fragment key={index}>
                <div className={`border rounded-lg p-4 mb-3 ${getFaseBackgroundColor(fase.nome_fase)}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">
                        {fase.nome_fase === 'Remoção' ? '🚑' : 
                         fase.nome_fase === 'Clínica' ? '🏥' : 
                         fase.nome_fase === 'Velório' ? '⚰️' : 
                         fase.nome_fase === 'Sepultamento' ? '🪦' : '📍'}
                      </span>
                      <input
                        type="text"
                        value={fase.nome_fase}
                        onChange={(e) => {
                          const newFases = [...formData.fases];
                          newFases[index].nome_fase = e.target.value;
                          setFormData({...formData, fases: newFases});
                        }}
                        className="text-sm font-medium text-gray-700 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Nome da fase"
                      />
                      <span className="text-xs text-gray-500">(Ordem: {fase.ordem})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newFases = formData.fases.filter((_, i) => i !== index);
                        setFormData({...formData, fases: newFases});
                      }}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remover
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Nome do Local
                      </label>
                      <input
                        type="text"
                        value={fase.nome_local || ''}
                        onChange={(e) => {
                          const newFases = [...formData.fases];
                          newFases[index].nome_local = e.target.value;
                          setFormData({...formData, fases: newFases});
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Nome do local"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Endereço de Destino
                      </label>
                      <input
                        type="text"
                        value={fase.endereco_destino}
                        onChange={(e) => {
                          const newFases = [...formData.fases];
                          newFases[index].endereco_destino = e.target.value;
                          setFormData({...formData, fases: newFases});
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Endereço completo"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Data
                      </label>
                      <input
                        type="date"
                        value={fase.data || ''}
                        onChange={(e) => {
                          const newFases = [...formData.fases];
                          newFases[index].data = e.target.value;
                          setFormData({...formData, fases: newFases});
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Horário
                      </label>
                      <input
                        type="time"
                        value={fase.horario || ''}
                        onChange={(e) => {
                          const newFases = [...formData.fases];
                          newFases[index].horario = e.target.value;
                          setFormData({...formData, fases: newFases});
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>
                </div>
                {fase.nome_fase === 'Velório' &&
                  !selectedOS &&
                  possuiFasesVelorioSepultamento(formData.fases) &&
                  renderCheckboxVelorioSepultamentoMesmoLocal(
                    Boolean(formData.velorio_sepultamento_mesmo_local),
                    (checked) =>
                      setFormData({
                        ...formData,
                        velorio_sepultamento_mesmo_local: checked
                      })
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {selectedOS ? 'Adicionar Fases' : 'Criar OS'}
              </button>
              <button
                type="button"
                onClick={cancelCreateForm}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
          </div>
        )}
      </div>
      
      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div 
          onClick={() => setActiveTab('pendentes')}
          className={`bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all hover:shadow-lg border-2 ${
            activeTab === 'pendentes' ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 hover:border-yellow-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">OSs Pendentes</p>
              <p className="text-3xl font-bold text-gray-900">{ordensSemMonitoramento.length}</p>
              <p className="text-xs text-gray-500 mt-1">Aguardando início de monitoramento</p>
            </div>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              activeTab === 'pendentes' ? 'bg-yellow-400' : 'bg-yellow-100'
            }`}>
              <span className="text-3xl">⏳</span>
            </div>
          </div>
        </div>
        
        <div 
          onClick={() => setActiveTab('monitoramento')}
          className={`bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all hover:shadow-lg border-2 ${
            activeTab === 'monitoramento' ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">OSs em Monitoramento</p>
              <p className="text-3xl font-bold text-gray-900">
                {ordensComMonitoramento.length}
                {ordensComMonitoramentoBase.length !== ordensComMonitoramento.length && (
                  <span className="text-lg text-gray-500">/{ordensComMonitoramentoBase.length}</span>
                )}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {mostrarTodasOSs ? 'Mostrando todas' : 'Apenas não concluídas'}
              </p>
            </div>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              activeTab === 'monitoramento' ? 'bg-blue-400' : 'bg-blue-100'
            }`}>
              <span className="text-3xl">📊</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sistema de Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('pendentes')}
              className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'pendentes'
                  ? 'border-yellow-500 text-yellow-600 bg-yellow-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <span>Pendentes</span>
                {ordensSemMonitoramento.length > 0 && (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    activeTab === 'pendentes'
                      ? 'bg-yellow-200 text-yellow-800'
                      : 'bg-gray-200 text-gray-800'
                  }`}>
                    {ordensSemMonitoramento.length}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('monitoramento')}
              className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'monitoramento'
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <span>Em Monitoramento</span>
                {(() => {
                  const totalOSs = ordensComMonitoramentoBase.length;
                  const osExibidas = ordensComMonitoramento.length;
                  
                  if (totalOSs > 0) {
                    return (
                      <div className="flex items-center gap-1">
                        <span 
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-all hover:scale-110 ${
                            activeTab === 'monitoramento'
                              ? 'bg-blue-200 text-blue-800'
                              : 'bg-gray-200 text-gray-800'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMostrarTodasOSs(!mostrarTodasOSs);
                          }}
                          title={mostrarTodasOSs ? 'Mostrar apenas não concluídas' : 'Mostrar todas (incluindo concluídas)'}
                        >
                          {osExibidas}{totalOSs !== osExibidas && `/${totalOSs}`}
                        </span>
                        {(() => {
                          const totalAlertas = ordensComMonitoramentoBase
                            .filter(os => !os.isSisfuner)
                            .reduce((acc, os) => {
                              const { alertas } = getOSAlertas(os);
                              return acc + alertas.length;
                            }, 0);
                          
                          if (totalAlertas > 0) {
                            const temAtrasada = ordensComMonitoramentoBase
                              .filter(os => !os.isSisfuner)
                              .some(os => getOSAlertas(os).temAtrasada);
                            
                            return (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                                temAtrasada 
                                  ? 'bg-red-500 text-white animate-pulse' 
                                  : 'bg-orange-400 text-white'
                              }`} title={`${totalAlertas} fase(s) com alerta`}>
                                ⚠️ {totalAlertas}
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </button>
          </nav>
        </div>

        {/* Conteúdo da Tab Pendentes */}
        {activeTab === 'pendentes' && (
          <div>
            {ordensSemMonitoramento.length > 0 ? (
              <>
        {/* Cards para Mobile */}
        <div className="md:hidden space-y-4 p-4">
           {currentOrdensSemMonitoramento.map((os) => {
             const isSisfuner = os.isSisfuner === true;
             const key = isSisfuner ? `sisfuner-${os._DADOSOS_NumOrdem}` : `nexus-${os.id}`;
             
             return (
               <div 
                 key={key} 
                 className={`bg-white border rounded-lg shadow-sm p-4 ${isSisfuner ? 'border-blue-200 bg-blue-50' : 'border-gray-200'}`}
               >
                 {/* Header do Card */}
                 <div className="flex items-start justify-between mb-3">
                   <div className="flex-1">
                     <h3 className="text-base font-semibold text-gray-900 mb-1">
                       {os.nome_falecido}
                     </h3>
                     {!isSisfuner && os.codigo_acesso && (
                       <p className="text-xs text-gray-500 font-mono">
                         Código: {os.codigo_acesso}
                       </p>
                     )}
                     {isSisfuner && os._DADOSOS_NumOrdem && (
                       <p className="text-xs text-gray-500">
                         N.C.: {os._DADOSOS_NumOrdem}
                       </p>
                     )}
                   </div>
                   <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(os.status_geral)}`}>
                     {os.status_geral}
                   </span>
                 </div>

                 {/* Informações do Card */}
                 <div className="space-y-2 mb-3">
                   {!isSisfuner && os.numero_declaracao_obito && (
                     <div className="flex items-center text-sm">
                       <span className="text-gray-600 font-medium w-20">D.O.:</span>
                       <span className="text-gray-900">{os.numero_declaracao_obito}</span>
                     </div>
                   )}
                   {!isSisfuner && os.numero_nota_contratacao && (
                     <div className="flex items-center text-sm">
                       <span className="text-gray-600 font-medium w-20">N.C.:</span>
                       <span className="text-gray-900">{os.numero_nota_contratacao}</span>
                     </div>
                   )}
                   <div className="flex items-center text-sm">
                     <span className="text-gray-600 font-medium w-20">Criado:</span>
                     <span className="text-gray-900">
                       {new Date(isSisfuner ? (os._DADOSOS_DataInsert || os.criado_em) : os.criado_em).toLocaleDateString('pt-BR')}
                     </span>
                   </div>
                   {!isSisfuner && os.fases && os.fases.length > 0 && (
                     <div className="flex items-start text-sm">
                       <span className="text-gray-600 font-medium w-20">Fases:</span>
                       <div className="flex-1">
                         <div className="flex items-center gap-1 mb-1">
                           <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                             {os.fases.length} fases
                           </span>
                           <div className="flex gap-1">
                             {os.fases.slice(0, 4).map((fase, index) => (
                               <span key={index} className="text-base" title={`${fase.nome_fase}: ${fase.status_fase}`}>
                                 {fase.nome_fase === 'Remoção' ? '🚑' : 
                                  fase.nome_fase === 'Clínica' ? '🏥' : 
                                  fase.nome_fase === 'Velório' ? '⚰️' : '🪦'}
                               </span>
                             ))}
                           </div>
                         </div>
                         {os.fases.some(f => f.motorista) && (
                           <div className="text-xs text-blue-600 space-y-0.5">
                             {os.fases.filter(f => f.motorista).slice(0, 2).map((fase, index) => (
                               <div key={index}>
                                 • {fase.nome_fase}: {fase.motorista?.nome}{fase.veiculo ? ` - ${formatarVeiculo(fase.veiculo)}` : ''}
                               </div>
                             ))}
                             {os.fases.filter(f => f.motorista).length > 2 && (
                               <div className="text-gray-500">
                                 +{os.fases.filter(f => f.motorista).length - 2} mais
                               </div>
                             )}
                           </div>
                         )}
                       </div>
                     </div>
                   )}
                 </div>

                 {/* Ações do Card */}
                 <div className="pt-3 border-t border-gray-200">
                   {isSisfuner ? (
                     <button 
                       onClick={() => handleIniciarMonitoramento(os)}
                       className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                     >
                       Iniciar Monitoramento
                     </button>
                   ) : (
                     <div className="flex gap-2">
                       <button 
                         onClick={() => handleViewOS(os)}
                         className="flex-1 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg hover:bg-blue-100 text-sm font-medium transition-colors"
                       >
                         Ver
                       </button>
                       <button 
                         onClick={() => handleEditOS(os)}
                         className="flex-1 bg-green-50 text-green-600 px-3 py-2 rounded-lg hover:bg-green-100 text-sm font-medium transition-colors"
                       >
                         Editar
                       </button>
                       <button 
                         onClick={() => handleOpenMotoristaModal(os)}
                         className="bg-purple-50 text-purple-600 px-3 py-2 rounded-lg hover:bg-purple-100 text-sm font-medium transition-colors"
                         title="Atribuir Motorista"
                       >
                         🚗
                       </button>
                       <button 
                         onClick={() => setEtiquetaOS(os)}
                         className="bg-amber-50 text-amber-800 px-3 py-2 rounded-lg hover:bg-amber-100 text-sm font-medium transition-colors"
                         title="Etiquetas"
                       >
                         Etiquetas
                       </button>
                     </div>
                   )}
                 </div>
               </div>
             );
           })}
         </div>

        {/* Tabela para Desktop */}
        <div className="hidden md:block overflow-x-auto w-full">
          <table className="w-full divide-y divide-gray-200">
             <thead className="bg-gray-50 sticky top-0 z-10">
               <tr>
                 <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Código
                 </th>
                 <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Falecido
                 </th>
                 <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   D.O.
                 </th>
                 <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   N.C.
                 </th>
                 <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Status
                 </th>
                 <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Fases
                 </th>
                 <th className="hidden md:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Criado em
                 </th>
                 <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Ações
                 </th>
               </tr>
             </thead>
                       <tbody className="bg-white divide-y divide-gray-200">
                {currentOrdensSemMonitoramento.map((os) => {
                 const isSisfuner = os.isSisfuner === true;
                 const key = isSisfuner ? `sisfuner-${os._DADOSOS_NumOrdem}` : `nexus-${os.id}`;
                 
                 return (
                 <tr key={key} className={`hover:bg-gray-50 ${isSisfuner ? 'bg-blue-50' : ''}`}>
                   <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                     {isSisfuner ? (
                       <span className="text-gray-400 italic">-</span>
                     ) : (
                       os.codigo_acesso
                     )}
                   </td>
                   <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                     {os.nome_falecido}
                   </td>
                   <td className="hidden sm:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                     {isSisfuner ? (
                       <span className="text-gray-400 italic">-</span>
                     ) : (
                       os.numero_declaracao_obito || '-'
                     )}
                   </td>
                   <td className="hidden lg:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                     {isSisfuner ? os._DADOSOS_NumOrdem : os.numero_nota_contratacao}
                   </td>
                   <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                     <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(os.status_geral)}`}>
                       {os.status_geral}
                     </span>
                   </td>
                   <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                     {isSisfuner ? (
                       <span className="text-gray-400 italic">-</span>
                     ) : (
                       <div className="space-y-1">
                         <div className="flex items-center gap-1">
                           <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                             {os.fases?.length || 0} fases
                           </span>
                           {os.fases && os.fases.length > 0 && (
                             <div className="flex gap-1">
                               {os.fases.map((fase, index) => {
                                 const alertaStatus = getFaseAlertaStatus(fase);
                                 const tempoInfo = formatarTempoRestante(fase);
                                 const tooltipText = `${fase.nome_fase}: ${fase.status_fase}${fase.motorista ? ` - ${fase.motorista.nome}${fase.veiculo ? ` - ${formatarVeiculo(fase.veiculo)}` : ''}` : ''}${tempoInfo ? ` - ${tempoInfo}` : ''}`;
                                 
                                 return (
                                   <div key={index} className="relative inline-block" title={tooltipText}>
                                     <span className={`text-lg inline-block ${alertaStatus ? 'opacity-50' : ''}`}>
                                       {fase.nome_fase === 'Remoção' ? '🚑' : 
                                        fase.nome_fase === 'Clínica' ? '🏥' : 
                                        fase.nome_fase === 'Velório' ? '⚰️' : '🪦'}
                                     </span>
                                     {alertaStatus && (
                                       <span 
                                         className={`absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-[8px] font-bold z-10 ${
                                           alertaStatus === 'atrasada' 
                                             ? 'bg-red-500 text-white animate-pulse' 
                                             : 'bg-orange-400 text-white'
                                         }`}
                                         title={tooltipText}
                                       >
                                         ⚠️
                                       </span>
                                     )}
                                   </div>
                                 );
                               })}
                             </div>
                           )}
                         </div>
                         
                         {os.fases && os.fases.some(f => f.motorista) && (
                           <div className="text-xs text-blue-600">
                             {os.fases.filter(f => f.motorista).map((fase, index) => (
                               <div key={index} className="flex items-center gap-1">
                                 <span className="text-xs">• {fase.nome_fase}: {fase.motorista?.nome}</span>
                               </div>
                             ))}
                           </div>
                         )}
                       </div>
                     )}
                   </td>
                   <td className="hidden md:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                     {new Date(isSisfuner ? (os._DADOSOS_DataInsert || os.criado_em) : os.criado_em).toLocaleDateString('pt-BR')}
                   </td>
                   <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                     {isSisfuner ? (
                       <button 
                         onClick={() => handleIniciarMonitoramento(os)}
                         className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs sm:text-sm transition-colors"
                       >
                         Iniciar Monitoramento
                       </button>
                     ) : (
                       <div className="flex flex-col sm:flex-row gap-2">
                         <button 
                           onClick={() => handleViewOS(os)}
                           className="text-blue-600 hover:text-blue-900 text-xs sm:text-sm"
                         >
                           Ver
                         </button>
                         <button 
                           onClick={() => handleEditOS(os)}
                           className="text-green-600 hover:text-green-900 text-xs sm:text-sm"
                         >
                           Editar
                         </button>
                         <button 
                           onClick={() => handleOpenMotoristaModal(os)}
                           className="text-purple-600 hover:text-purple-900 text-xs sm:text-sm"
                           title="Atribuir Motorista"
                         >
                           🚗
                         </button>
                         <button 
                           onClick={() => setEtiquetaOS(os)}
                           className="text-amber-700 hover:text-amber-900 text-xs sm:text-sm"
                           title="Etiquetas"
                         >
                           Etiquetas
                         </button>
                       </div>
                     )}
                   </td>
                 </tr>
               )})}
                          </tbody>
           </table>
         </div>
         
         {/* Paginação */}
          {ordensSemMonitoramento.length > 0 && totalPagesSemMonitoramento > 1 && (
           <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
             <div className="flex items-center justify-between">
               <div className="flex-1 flex justify-between sm:hidden">
                 <button
                    onClick={() => setCurrentPageSemMonitoramento(prev => Math.max(1, prev - 1))}
                    disabled={currentPageSemMonitoramento === 1}
                   className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   Anterior
                 </button>
                 <button
                    onClick={() => setCurrentPageSemMonitoramento(prev => Math.min(totalPagesSemMonitoramento, prev + 1))}
                    disabled={currentPageSemMonitoramento === totalPagesSemMonitoramento}
                   className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   Próxima
                 </button>
               </div>
               <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                 <div>
                   <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{startIndexSemMonitoramento + 1}</span> até{' '}
                      <span className="font-medium">{Math.min(endIndexSemMonitoramento, ordensSemMonitoramento.length)}</span> de{' '}
                      <span className="font-medium">{ordensSemMonitoramento.length}</span> resultados
                   </p>
                 </div>
                 <div>
                   <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                     <button
                        onClick={() => setCurrentPageSemMonitoramento(prev => Math.max(1, prev - 1))}
                        disabled={currentPageSemMonitoramento === 1}
                       className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                       <span className="sr-only">Anterior</span>
                       ←
                     </button>
                      {Array.from({ length: totalPagesSemMonitoramento }, (_, i) => i + 1).map((page) => {
                       if (
                         page === 1 ||
                          page === totalPagesSemMonitoramento ||
                          (page >= currentPageSemMonitoramento - 1 && page <= currentPageSemMonitoramento + 1)
                       ) {
                         return (
                           <button
                             key={page}
                              onClick={() => setCurrentPageSemMonitoramento(page)}
                             className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPageSemMonitoramento === page
                                 ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                 : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                             }`}
                           >
                             {page}
                           </button>
                         );
                        } else if (page === currentPageSemMonitoramento - 2 || page === currentPageSemMonitoramento + 2) {
                         return (
                           <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                             ...
                           </span>
                         );
                       }
                       return null;
                     })}
                     <button
                        onClick={() => setCurrentPageSemMonitoramento(prev => Math.min(totalPagesSemMonitoramento, prev + 1))}
                        disabled={currentPageSemMonitoramento === totalPagesSemMonitoramento}
                       className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                       <span className="sr-only">Próxima</span>
                       →
                     </button>
                   </nav>
                 </div>
               </div>
             </div>
           </div>
            )}
                </>
              ) : (
                <div className="text-center py-12 px-4">
                  <div className="text-gray-400 text-6xl mb-4">✅</div>
                  <p className="text-gray-500 text-lg font-medium mb-2">Nenhuma OS pendente</p>
                  <p className="text-gray-400 text-sm">Todas as OSs já foram iniciadas</p>
                </div>
              )}
           </div>
         )}
         
        {/* Conteúdo da Tab Monitoramento */}
        {activeTab === 'monitoramento' && (
          <div>
            {ordensComMonitoramento.length > 0 ? (
              <>
                {/* Cards para Mobile */}
                <div className="md:hidden space-y-4 p-4">
                  {currentOrdensComMonitoramento.map((os) => {
            const isSisfuner = os.isSisfuner === true;
            const key = isSisfuner ? `sisfuner-${os._DADOSOS_NumOrdem}` : `nexus-${os.id}`;
            const { temAlerta, temAtrasada } = !isSisfuner ? getOSAlertas(os) : { temAlerta: false, temAtrasada: false };
            
            return (
              <div 
                key={key} 
                className={`bg-white border rounded-lg shadow-sm p-4 ${
                  isSisfuner ? 'border-blue-200 bg-blue-50' : ''
                } ${
                  temAtrasada 
                    ? 'border-l-4 border-red-500 bg-red-50' 
                    : temAlerta 
                    ? 'border-l-4 border-orange-400 bg-orange-50' 
                    : 'border-gray-200'
                }`}
              >
                {/* Header do Card */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      {os.nome_falecido}
                    </h3>
                    {!isSisfuner && os.codigo_acesso && (
                      <p className="text-xs text-gray-500 font-mono">
                        Código: {os.codigo_acesso}
                      </p>
                    )}
                    {isSisfuner && os._DADOSOS_NumOrdem && (
                      <p className="text-xs text-gray-500">
                        N.C.: {os._DADOSOS_NumOrdem}
                      </p>
                    )}
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(os.status_geral)}`}>
                    {os.status_geral}
                  </span>
                </div>

                {/* Informações do Card */}
                <div className="space-y-2 mb-3">
                  {!isSisfuner && os.numero_declaracao_obito && (
                    <div className="flex items-center text-sm">
                      <span className="text-gray-600 font-medium w-20">D.O.:</span>
                      <span className="text-gray-900">{os.numero_declaracao_obito}</span>
                    </div>
                  )}
                  {!isSisfuner && os.numero_nota_contratacao && (
                    <div className="flex items-center text-sm">
                      <span className="text-gray-600 font-medium w-20">N.C.:</span>
                      <span className="text-gray-900">{os.numero_nota_contratacao}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm">
                    <span className="text-gray-600 font-medium w-20">Criado:</span>
                    <span className="text-gray-900">
                      {new Date(isSisfuner ? (os._DADOSOS_DataInsert || os.criado_em) : os.criado_em).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  {!isSisfuner && os.fases && os.fases.length > 0 && (
                    <div className="flex items-start text-sm">
                      <span className="text-gray-600 font-medium w-20">Fases:</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-1 mb-1 flex-wrap">
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {os.fases.length} fases
                          </span>
                          <div className="flex gap-1 items-center">
                            {os.fases.map((fase, index) => {
                              const alertaStatus = getFaseAlertaStatus(fase);
                              const tempoInfo = formatarTempoRestante(fase);
                              const tooltipText = `${fase.nome_fase}: ${fase.status_fase}${fase.motorista ? ` - ${fase.motorista.nome}` : ''}${tempoInfo ? ` - ${tempoInfo}` : ''}`;
                              
                              return (
                                <div key={index} className="relative inline-block" title={tooltipText}>
                                  <span className={`text-base inline-block ${alertaStatus ? 'opacity-50' : ''}`}>
                                    {fase.nome_fase === 'Remoção' ? '🚑' : 
                                     fase.nome_fase === 'Clínica' ? '🏥' : 
                                     fase.nome_fase === 'Velório' ? '⚰️' : '🪦'}
                                  </span>
                                  {alertaStatus && (
                                    <span 
                                      className={`absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-[8px] font-bold z-10 ${
                                        alertaStatus === 'atrasada' 
                                          ? 'bg-red-500 text-white animate-pulse' 
                                          : 'bg-orange-400 text-white'
                                      }`}
                                      title={tooltipText}
                                    >
                                      ⚠️
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        {os.fases.some(f => f.motorista) && (
                          <div className="text-xs text-blue-600 space-y-0.5">
                            {os.fases.filter(f => f.motorista).slice(0, 2).map((fase, index) => {
                              const alertaStatus = getFaseAlertaStatus(fase);
                              const tempoInfo = formatarTempoRestante(fase);
                              return (
                                <div key={index} className="flex items-center gap-1">
                                  <span>• {fase.nome_fase}: {fase.motorista?.nome}{fase.veiculo ? ` - ${fase.veiculo.placa}${fase.veiculo.prefixo ? ` (${fase.veiculo.prefixo})` : ''}` : ''}</span>
                                  {alertaStatus && (
                                    <span 
                                      className={`inline-flex items-center px-1 py-0.5 rounded text-[10px] font-bold ${
                                        alertaStatus === 'atrasada' 
                                          ? 'bg-red-500 text-white' 
                                          : 'bg-orange-400 text-white'
                                      }`}
                                      title={tempoInfo}
                                    >
                                      ⚠️
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                            {os.fases.filter(f => f.motorista).length > 2 && (
                              <div className="text-gray-500">
                                +{os.fases.filter(f => f.motorista).length - 2} mais
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Ações do Card */}
                <div className="pt-3 border-t border-gray-200">
                  {isSisfuner ? (
                    <button 
                      onClick={() => handleIniciarMonitoramento(os)}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                    >
                      Iniciar Monitoramento
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleViewOS(os)}
                        className="flex-1 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg hover:bg-blue-100 text-sm font-medium transition-colors"
                      >
                        Ver
                      </button>
                      <button 
                        onClick={() => handleEditOS(os)}
                        className="flex-1 bg-green-50 text-green-600 px-3 py-2 rounded-lg hover:bg-green-100 text-sm font-medium transition-colors"
                      >
                        Editar
                      </button>
                      <button 
                        onClick={() => handleOpenMotoristaModal(os)}
                        className="bg-purple-50 text-purple-600 px-3 py-2 rounded-lg hover:bg-purple-100 text-sm font-medium transition-colors"
                        title="Atribuir Motorista"
                      >
                        🚗
                      </button>
                      <button 
                        onClick={() => setEtiquetaOS(os)}
                        className="bg-amber-50 text-amber-800 px-3 py-2 rounded-lg hover:bg-amber-100 text-sm font-medium transition-colors"
                        title="Etiquetas"
                      >
                        Etiquetas
                      </button>
                    </div>
               )}
             </div>
              </div>
            );
          })}
        </div>

        {/* Tabela para Desktop */}
        <div className="hidden md:block overflow-x-auto w-full">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Falecido
                </th>
                <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  D.O.
                </th>
                <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  N.C.
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fases
                </th>
                <th className="hidden md:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Criado em
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentOrdensComMonitoramento.map((os) => {
                const isSisfuner = os.isSisfuner === true;
                const key = isSisfuner ? `sisfuner-${os._DADOSOS_NumOrdem}` : `nexus-${os.id}`;
                const { temAlerta, temAtrasada } = !isSisfuner ? getOSAlertas(os) : { temAlerta: false, temAtrasada: false };
                
                return (
                  <tr 
                    key={key} 
                    className={`hover:bg-gray-50 ${
                      isSisfuner ? 'bg-blue-50' : ''
                    } ${
                      temAtrasada 
                        ? 'border-l-4 border-red-500 bg-red-50' 
                        : temAlerta 
                        ? 'border-l-4 border-orange-400 bg-orange-50' 
                        : ''
                    }`}
                  >
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {isSisfuner ? (
                        <span className="text-gray-400 italic">-</span>
                      ) : (
                        os.codigo_acesso
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {os.nome_falecido}
                    </td>
                    <td className="hidden sm:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {isSisfuner ? (
                        <span className="text-gray-400 italic">-</span>
                      ) : (
                        os.numero_declaracao_obito || '-'
                      )}
                    </td>
                    <td className="hidden lg:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {isSisfuner ? os._DADOSOS_NumOrdem : os.numero_nota_contratacao}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(os.status_geral)}`}>
                        {os.status_geral}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {isSisfuner ? (
                        <span className="text-gray-400 italic">-</span>
                      ) : (
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {os.fases?.length || 0} fases
                            </span>
                            {os.fases && os.fases.length > 0 && (
                              <div className="flex gap-1 items-center">
                                {os.fases.map((fase, index) => {
                                  const alertaStatus = getFaseAlertaStatus(fase);
                                  const tempoInfo = formatarTempoRestante(fase);
                                  const tooltipText = `${fase.nome_fase}: ${fase.status_fase}${fase.motorista ? ` - ${fase.motorista.nome}${fase.veiculo ? ` - ${formatarVeiculo(fase.veiculo)}` : ''}` : ''}${tempoInfo ? ` - ${tempoInfo}` : ''}`;
                                  
                                  return (
                                    <div key={index} className="relative inline-block" title={tooltipText}>
                                      <span className={`text-lg inline-block ${alertaStatus ? 'opacity-50' : ''}`}>
                                        {fase.nome_fase === 'Remoção' ? '🚑' : 
                                         fase.nome_fase === 'Clínica' ? '🏥' : 
                                         fase.nome_fase === 'Velório' ? '⚰️' : '🪦'}
                                      </span>
                                      {alertaStatus && (
                                        <span 
                                          className={`absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-[8px] font-bold z-10 ${
                                            alertaStatus === 'atrasada' 
                                              ? 'bg-red-500 text-white animate-pulse' 
                                              : 'bg-orange-400 text-white'
                                          }`}
                                          title={tooltipText}
                                        >
                                          ⚠️
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          
                          {os.fases && os.fases.some(f => f.motorista) && (
                            <div className="text-xs text-blue-600">
                              {os.fases.filter(f => f.motorista).map((fase, index) => {
                                const alertaStatus = getFaseAlertaStatus(fase);
                                const tempoInfo = formatarTempoRestante(fase);
                                return (
                                  <div key={index} className="flex items-center gap-1">
                                    <span className="text-xs">• {fase.nome_fase}: {fase.motorista?.nome}</span>
                                    {alertaStatus && (
                                      <span 
                                        className={`inline-flex items-center px-1 py-0.5 rounded text-[10px] font-bold ${
                                          alertaStatus === 'atrasada' 
                                            ? 'bg-red-500 text-white' 
                                            : 'bg-orange-400 text-white'
                                        }`}
                                        title={tempoInfo}
                                      >
                                        ⚠️
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="hidden md:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(isSisfuner ? (os._DADOSOS_DataInsert || os.criado_em) : os.criado_em).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {isSisfuner ? (
               <button
                          onClick={() => handleIniciarMonitoramento(os)}
                          className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs sm:text-sm transition-colors"
               >
                          Iniciar Monitoramento
               </button>
                      ) : (
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button 
                            onClick={() => handleViewOS(os)}
                            className="text-blue-600 hover:text-blue-900 text-xs sm:text-sm"
                          >
                            Ver
                          </button>
                          <button 
                            onClick={() => handleEditOS(os)}
                            className="text-green-600 hover:text-green-900 text-xs sm:text-sm"
                          >
                            Editar
                          </button>
                          <button 
                            onClick={() => handleOpenMotoristaModal(os)}
                            className="text-purple-600 hover:text-purple-900 text-xs sm:text-sm"
                            title="Atribuir Motorista"
                          >
                            🚗
                          </button>
                          <button 
                            onClick={() => setEtiquetaOS(os)}
                            className="text-amber-700 hover:text-amber-900 text-xs sm:text-sm"
                            title="Etiquetas"
                          >
                            Etiquetas
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Paginação */}
        {ordensComMonitoramento.length > 0 && totalPagesComMonitoramento > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPageComMonitoramento(prev => Math.max(1, prev - 1))}
                  disabled={currentPageComMonitoramento === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPageComMonitoramento(prev => Math.min(totalPagesComMonitoramento, prev + 1))}
                  disabled={currentPageComMonitoramento === totalPagesComMonitoramento}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próxima
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Mostrando <span className="font-medium">{startIndexComMonitoramento + 1}</span> até{' '}
                    <span className="font-medium">{Math.min(endIndexComMonitoramento, ordensComMonitoramento.length)}</span> de{' '}
                    <span className="font-medium">{ordensComMonitoramento.length}</span> resultados
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPageComMonitoramento(prev => Math.max(1, prev - 1))}
                      disabled={currentPageComMonitoramento === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Anterior</span>
                      ←
                    </button>
                    {Array.from({ length: totalPagesComMonitoramento }, (_, i) => i + 1).map((page) => {
                      if (
                        page === 1 ||
                        page === totalPagesComMonitoramento ||
                        (page >= currentPageComMonitoramento - 1 && page <= currentPageComMonitoramento + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPageComMonitoramento(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPageComMonitoramento === page
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPageComMonitoramento - 2 || page === currentPageComMonitoramento + 2) {
                        return (
                          <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                    <button
                      onClick={() => setCurrentPageComMonitoramento(prev => Math.min(totalPagesComMonitoramento, prev + 1))}
                      disabled={currentPageComMonitoramento === totalPagesComMonitoramento}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Próxima</span>
                      →
                    </button>
                  </nav>
                </div>
              </div>
            </div>
           </div>
         )}
         
              </>
            ) : (
              <div className="text-center py-12 px-4">
                <div className="text-gray-400 text-6xl mb-4">📋</div>
                <p className="text-gray-500 text-lg font-medium mb-2">Nenhuma OS em monitoramento</p>
                <p className="text-gray-400 text-sm">Inicie o monitoramento de uma OS para vê-la aqui</p>
            </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de Visualização Detalhada */}
      {showViewModal && selectedOS && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
             <div className="p-4 sm:p-6">
               <div className="flex justify-between items-center mb-6">
                 <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                   Detalhes da Ordem de Serviço
                 </h2>
                 <button
                   onClick={closeViewModal}
                   className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                 >
                   ×
                 </button>
               </div>
 
               {/* Informações principais */}
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Informações Gerais</h3>
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium text-gray-700">Código de Acesso:</span>
                      <span className="ml-2 text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">
                        {selectedOS.codigo_acesso}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Nome do Falecido:</span>
                      <span className="ml-2 text-gray-900">{selectedOS.nome_falecido}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Status Geral:</span>
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedOS.status_geral)}`}>
                        {selectedOS.status_geral}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Criado em:</span>
                      <span className="ml-2 text-gray-900">
                        {new Date(selectedOS.criado_em).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                                         <div>
                       <span className="font-medium text-gray-700">Motoristas Atribuídos:</span>
                       <span className="ml-2 text-gray-900">
                         {selectedOS.fases && selectedOS.fases.length > 0 ? (
                           selectedOS.fases.filter(fase => fase.motorista).length > 0 ? (
                             <div className="space-y-1">
                               {selectedOS.fases.filter(fase => fase.motorista).map((fase, index) => (
                                 <div key={index} className="text-sm">
                                   <span className="text-blue-600 font-medium">
                                     {fase.nome_fase}: {fase.motorista?.nome} ({fase.motorista?.email}){fase.veiculo ? ` - ${formatarVeiculo(fase.veiculo)}` : ''}
                                   </span>
                                 </div>
                               ))}
                             </div>
                           ) : (
                             <span className="text-gray-500 italic">Nenhum motorista atribuído</span>
                           )
                         ) : (
                           <span className="text-gray-500 italic">Carregando...</span>
                         )}
                       </span>
                     </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Documentação</h3>
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium text-gray-700">Nº Declaração de Óbito:</span>
                      <span className="ml-2 text-gray-900">{selectedOS.numero_declaracao_obito || 'Não informado'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Nº Nota de Contratação:</span>
                      <span className="ml-2 text-gray-900">{selectedOS.numero_nota_contratacao || 'Não informado'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Observações:</span>
                      <span className="ml-2 text-gray-900">{selectedOS.observacoes || 'Nenhuma observação'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* WhatsApp de acompanhamento da família */}
              <div className="mb-6 border border-green-200 bg-green-50 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">WhatsApp da Família</h3>
                    <p className="text-sm text-gray-700 mt-1">
                      <span className="font-medium">Contratante:</span>{' '}
                      {selectedOS.contratante_nome || <span className="italic text-gray-500">Não informado</span>}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Telefone 1:</span>{' '}
                      {selectedOS.contratante_telefone1 || <span className="italic text-gray-500">Não informado</span>}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleReenviarWhatsApp(selectedOS)}
                    disabled={reenviandoWhatsApp || !selectedOS.contratante_telefone1}
                    title={!selectedOS.contratante_telefone1 ? 'Informe o Telefone 1 do contratante em Editar' : undefined}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {reenviandoWhatsApp
                      ? 'Enviando...'
                      : notificacoesWhatsApp.length > 0
                        ? 'Reenviar WhatsApp'
                        : 'Enviar WhatsApp'}
                  </button>
                </div>

                {loadingWhatsApp ? (
                  <p className="text-sm text-gray-500">Carregando histórico...</p>
                ) : notificacoesWhatsApp.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Nenhuma mensagem enviada para esta OS.</p>
                ) : (
                  <ul className="space-y-2">
                    {notificacoesWhatsApp.map((n) => {
                      const statusInfo = NOTIFICACAO_STATUS_LABEL[n.status];
                      return (
                        <li key={n.id} className="bg-white border border-gray-200 rounded-md px-3 py-2 text-sm">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${statusInfo.className}`}>
                              {statusInfo.label}
                            </span>
                            <span className="text-gray-700">
                              {new Date(n.criado_em).toLocaleString('pt-BR')}
                            </span>
                            <span className="text-gray-500">
                              {n.automatico ? 'Automático' : `Manual${n.usuario_nome ? ` por ${n.usuario_nome}` : ''}`}
                            </span>
                            {n.telefone && <span className="text-gray-500 font-mono">{n.telefone}</span>}
                          </div>
                          {n.erro && <p className="text-xs text-red-700 mt-1">{n.erro}</p>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Fases da OS */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Fases da OS</h3>
                <div className="space-y-3">
                  {selectedOS.fases && Array.isArray(selectedOS.fases) && selectedOS.fases.length > 0 ? (
                    selectedOS.fases.map((fase, index) => (
                      <div key={index} className={`border rounded-lg p-4 ${getFaseBackgroundColor(fase.nome_fase)}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="text-xl">
                              {fase.nome_fase === 'Remoção' ? '🚑' : 
                               fase.nome_fase === 'Clínica' ? '🏥' : 
                               fase.nome_fase === 'Velório' ? '⚰️' : '🪦'}
                            </span>
                            <span className="text-sm font-medium text-gray-700">
                              {fase.nome_fase}
                            </span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              Ordem: {fase.ordem}
                            </span>
                          </div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(fase.status_fase)}`}>
                            {fase.status_fase}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-600">Endereço de Destino:</span>
                            <p className="text-gray-900 mt-1">{fase.endereco_destino || 'Não informado'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Motorista:</span>
                            <p className="text-gray-900 mt-1">
                              {fase.motorista ? (
                                `${fase.motorista.nome} (${fase.motorista.email})${fase.veiculo ? ` - Veículo: ${formatarVeiculo(fase.veiculo)}` : ''}`
                              ) : (
                                <span className="text-gray-500 italic">Não atribuído</span>
                              )}
                            </p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Data:</span>
                            <p className="text-gray-900 mt-1">
                              {fase.data_fase ? (() => {
                                try {
                                  let dateStr = fase.data_fase;
                                  
                                  // Se for string, extrair apenas a parte da data (YYYY-MM-DD)
                                  if (typeof dateStr === 'string') {
                                    // Remover parte de tempo se existir (YYYY-MM-DDTHH:MM:SS...)
                                    dateStr = dateStr.split('T')[0];
                                    
                                    // Verificar se está no formato YYYY-MM-DD
                                    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                      const [year, month, day] = dateStr.split('-');
                                      return `${day}/${month}/${year}`;
                                    }
                                  }
                                  
                                  // Se for objeto Date, usar métodos locais para evitar timezone
                                  if (dateStr instanceof Date) {
                                    const year = dateStr.getFullYear();
                                    const month = String(dateStr.getMonth() + 1).padStart(2, '0');
                                    const day = String(dateStr.getDate()).padStart(2, '0');
                                    return `${day}/${month}/${year}`;
                                  }
                                  
                                  return String(dateStr);
                                } catch {
                                  return fase.data_fase ? String(fase.data_fase) : 'Não informada';
                                }
                              })() : 'Não informada'}
                            </p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Horário:</span>
                            <p className="text-gray-900 mt-1">
                              {fase.horario || 'Não informado'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500 border border-gray-200 rounded-lg">
                      <p>Nenhuma fase encontrada para esta OS</p>
                      <p className="text-sm mt-1">Debug: fases = {JSON.stringify(selectedOS.fases)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Botão de fechar */}
              <div className="flex justify-end">
                <button
                  onClick={closeViewModal}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Edição */}
      {showEditModal && editData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
             <div className="p-4 sm:p-6">
               <div className="flex justify-between items-center mb-6">
                 <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                   Editar Ordem de Serviço
                 </h2>
                 <button
                   onClick={closeEditModal}
                   className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                 >
                   ×
                 </button>
               </div>
 
               <form onSubmit={handleSaveEdit}>
                 {/* Informações principais */}
                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome do Falecido
                    </label>
                    <input
                      type="text"
                      value={editData.nome_falecido}
                      onChange={(e) => setEditData({...editData, nome_falecido: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nº Declaração de Óbito
                    </label>
                    <input
                      type="text"
                      value={editData.numero_declaracao_obito || ''}
                      onChange={(e) => setEditData({...editData, numero_declaracao_obito: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nº Nota de Contratação
                    </label>
                    <input
                      type="text"
                      value={editData.numero_nota_contratacao || ''}
                      onChange={(e) => setEditData({...editData, numero_nota_contratacao: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Contratante (WhatsApp de acompanhamento) */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contratante</label>
                    <input
                      type="text"
                      value={editData.contratante_nome || ''}
                      onChange={(e) => setEditData({ ...editData, contratante_nome: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      maxLength={150}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefone 1 (WhatsApp)</label>
                    <input
                      type="tel"
                      value={editData.contratante_telefone1 || ''}
                      onChange={(e) => setEditData({ ...editData, contratante_telefone1: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="(11) 91234-5678"
                      maxLength={30}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefone 2</label>
                    <input
                      type="tel"
                      value={editData.contratante_telefone2 || ''}
                      onChange={(e) => setEditData({ ...editData, contratante_telefone2: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      maxLength={30}
                    />
                  </div>
                </div>

                {/* Observações */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Observações
                  </label>
                  <textarea
                    value={editData.observacoes || ''}
                    onChange={(e) => setEditData({...editData, observacoes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Observações gerais da OS"
                  />
                </div>

                {/* Fases da OS */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Fases da OS</h3>
                  <div className="space-y-3">
                    {editData.fases && Array.isArray(editData.fases) && editData.fases.length > 0 ? (
                      editData.fases.map((fase, index) => (
                        <React.Fragment key={fase.id || index}>
                        <div className={`border rounded-lg p-4 ${getFaseBackgroundColor(fase.nome_fase)}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                            <span className="text-xl">
                              {fase.nome_fase === 'Remoção' ? '🚑' : 
                               fase.nome_fase === 'Clínica' ? '🏥' : 
                               fase.nome_fase === 'Velório' ? '⚰️' : '🪦'}
                            </span>
                            <span className="text-sm font-medium text-gray-700">
                              {fase.nome_fase}
                            </span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              Ordem: {fase.ordem}
                            </span>
                            </div>
                            {podeExcluirFase(fase) && (
                              <button
                                type="button"
                                onClick={() => handleExcluirFase(fase, index)}
                                className="text-xs text-red-600 hover:text-red-800 hover:underline font-medium"
                              >
                                Excluir
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                     <div className="w-full">
                             <label className="block text-xs font-medium text-gray-600 mb-1">
                               Endereço de Destino
                             </label>
                             <input
                               type="text"
                               value={fase.endereco_destino || ''}
                               onChange={(e) => {
                                 const newFases = [...editData.fases];
                                 newFases[index].endereco_destino = e.target.value;
                                 setEditData({...editData, fases: newFases});
                               }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                               required
                             />
                           </div>
                           
                           {/* Status da Fase */}
                            <div className="w-full">
                             <label className="block text-xs font-medium text-gray-600 mb-1">
                               Status da Fase
                             </label>
                             <select
                               value={fase.status_fase || 'Pendente'}
                               onChange={(e) => {
                                 const newFases = [...editData.fases];
                                 newFases[index].status_fase = e.target.value;
                                 setEditData({...editData, fases: newFases});
                               }}
                               className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                             >
                               <option value="Pendente">Pendente</option>
                               <option value="Em Andamento">Em Andamento</option>
                               <option value="Concluída">Concluída</option>
                             </select>
                            </div>
                          </div>
                          
                          {/* Data e Horário */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                            <div className="w-full">
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Data da Fase
                              </label>
                              <input
                                type="date"
                                value={fase.data_fase ? (() => {
                                  try {
                                    let dateStr = fase.data_fase;
                                    
                                    // Se for string, extrair apenas a parte da data (YYYY-MM-DD)
                                    if (typeof dateStr === 'string') {
                                      // Remover parte de tempo se existir (YYYY-MM-DDTHH:MM:SS...)
                                      dateStr = dateStr.split('T')[0];
                                      
                                      // Verificar se está no formato YYYY-MM-DD
                                      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                        return dateStr;
                                      }
                                    }
                                    
                                    // Se for objeto Date, usar métodos locais para evitar timezone
                                    if (dateStr instanceof Date) {
                                      const year = dateStr.getFullYear();
                                      const month = String(dateStr.getMonth() + 1).padStart(2, '0');
                                      const day = String(dateStr.getDate()).padStart(2, '0');
                                      return `${year}-${month}-${day}`;
                                    }
                                    
                                    return '';
                                  } catch {
                                    return '';
                                  }
                                })() : ''}
                                onChange={(e) => {
                                  const newFases = [...editData.fases];
                                  newFases[index].data_fase = e.target.value || null;
                                  setEditData({...editData, fases: newFases});
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />
                            </div>
                            <div className="w-full">
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Horário da Fase
                              </label>
                              <input
                                type="time"
                                value={fase.horario || ''}
                                onChange={(e) => {
                                  const newFases = [...editData.fases];
                                  newFases[index].horario = e.target.value;
                                  setEditData({...editData, fases: newFases});
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />
                            </div>
                           </div>
                        </div>
                        {fase.nome_fase === 'Velório' &&
                          possuiFasesVelorioSepultamento(editData.fases) &&
                          renderCheckboxVelorioSepultamentoMesmoLocal(
                            Boolean(editData.velorio_sepultamento_mesmo_local),
                            (checked) =>
                              setEditData({
                                ...editData,
                                velorio_sepultamento_mesmo_local: checked
                              }),
                            {
                              disabled: fasesVelorioSepultamentoIniciadas(editData.fases),
                              showLockedMessage: fasesVelorioSepultamentoIniciadas(editData.fases)
                            }
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <div className="text-center py-4 text-gray-500 border border-gray-200 rounded-lg">
                        <p>Nenhuma fase encontrada para esta OS</p>
                        <p className="text-sm mt-2">As fases serão criadas automaticamente ao salvar</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Botões de ação */}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Salvar Alterações
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
                           {/* Modal para Atribuir Motorista à Próxima Fase */}
        {selectedOS && !showViewModal && !showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
              <div className="p-4 sm:p-6 flex-1 overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                    Atribuir Motorista às Fases
                  </h2>
                  <button
                    onClick={closeMotoristaModal}
                    className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>
               
               <div className="mb-4">
                 <p className="text-sm text-gray-600 mb-2">
                   OS: <span className="font-medium">{selectedOS.codigo_acesso}</span>
                 </p>
                 <p className="text-sm text-gray-600">
                   Falecido: <span className="font-medium">{selectedOS.nome_falecido}</span>
                 </p>
               </div>

               {/* Status das Fases */}
               <div className="mb-6">
                 <h3 className="text-sm font-medium text-gray-700 mb-3">Status das Fases:</h3>
                 <div className="space-y-3 max-h-60 overflow-y-auto">
                   {selectedOS.fases && selectedOS.fases.length > 0 ? (
                     selectedOS.fases.map((fase, index) => (
                       <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                         <div className="flex items-center justify-between mb-2">
                         <div className="flex items-center gap-2">
                           <span className="text-lg">
                             {fase.nome_fase === 'Remoção' ? '🚑' : 
                              fase.nome_fase === 'Clínica' ? '🏥' : 
                              fase.nome_fase === 'Velório' ? '⚰️' : '🪦'}
                           </span>
                           <span className="text-sm font-medium">{fase.nome_fase}</span>
                         </div>
                         <div className="flex items-center gap-2">
                           <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(fase.status_fase)}`}>
                             {fase.status_fase}
                           </span>
                           </div>
                         </div>
                         {fase.motorista && fase.id ? (
                           <div className="mt-2 pt-2 border-t border-gray-200">
                             <div className="flex items-center justify-between">
                               <div className="flex items-center gap-2">
                                 <span className="text-xs text-gray-600">Motorista:</span>
                                 <span className="text-xs font-medium text-blue-600">
                               {fase.motorista.nome}{fase.veiculo ? ` - ${formatarVeiculo(fase.veiculo)}` : ''}
                             </span>
                         </div>
                               {fase.status_fase === 'Pendente' && (
                                 <button
                                   onClick={() => handleRemoverMotorista(selectedOS.id, fase.id!, fase.nome_fase)}
                                   className="text-xs text-red-600 hover:text-red-800 hover:underline font-medium"
                                 >
                                   Remover
                                 </button>
                               )}
                             </div>
                           </div>
                         ) : (
                           <div className="mt-2 pt-2 border-t border-gray-200">
                             <span className="text-xs text-gray-500">Nenhum motorista atribuído</span>
                           </div>
                         )}
                       </div>
                     ))
                   ) : (
                     <p className="text-sm text-gray-500">Carregando fases...</p>
                   )}
                 </div>
               </div>

               {/* Seleção da Fase e Motorista */}
               <div className="mb-6">
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   Fase para Atribuir Motorista
                 </label>
                 {(() => {
                   // Filtrar fases disponíveis: apenas as que existem na OS e que ainda não têm motorista
                   const fasesDisponiveis = selectedOS.fases && selectedOS.fases.length > 0
                     ? selectedOS.fases.filter(
                         (fase) =>
                           !fase.motorista &&
                           !(selectedOS.velorio_sepultamento_mesmo_local && fase.nome_fase === 'Sepultamento')
                       )
                     : [];
                   
                   const getFaseIcon = (nomeFase: string) => {
                     switch (nomeFase) {
                       case 'Remoção': return '🚑';
                       case 'Clínica': return '🏥';
                       case 'Velório': return '⚰️';
                       case 'Sepultamento': return '🪦';
                       default: return '📍';
                     }
                   };

                   if (fasesDisponiveis.length === 0) {
                     return (
                       <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 mb-3">
                         {selectedOS.fases && selectedOS.fases.length > 0
                           ? 'Todas as fases já têm motorista atribuído'
                           : 'Nenhuma fase disponível nesta OS'}
                       </div>
                     );
                   }

                   return (
                     <select 
                       value={selectedFase}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                       onChange={(e) => setSelectedFase(e.target.value)}
                     >
                       <option value="">Selecione a fase...</option>
                       {fasesDisponiveis.map((fase, index) => (
                         <option key={index} value={fase.nome_fase}>
                           {getFaseIcon(fase.nome_fase)} {fase.nome_fase}
                         </option>
                       ))}
                     </select>
                   );
                 })()}

                 {/* Filtros de CMV */}
                 <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                   <h4 className="text-sm font-medium text-gray-700 mb-3">Filtros de CMV</h4>
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                     <div>
                       <label className="block text-xs font-medium text-gray-600 mb-1">
                         Data do CMV
                       </label>
                       <input
                         type="date"
                         value={cmvFilters.data}
                         onChange={(e) => {
                           setCmvFilters({...cmvFilters, data: e.target.value});
                         }}
                         className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                       />
                     </div>
                     <div>
                       <label className="block text-xs font-medium text-gray-600 mb-1">
                         Turno
                       </label>
                       <select
                         value={cmvFilters.turno}
                         onChange={(e) => {
                           setCmvFilters({...cmvFilters, turno: e.target.value});
                         }}
                         className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                       >
                         <option value="">Todos</option>
                         <option value="Manhã - 12x36 - 06hrs as 18hrs">Manhã - 12x36 - 06hrs as 18hrs</option>
                         <option value="Noite - 12x36 - 18hrs as 06hrs">Noite - 12x36 - 18hrs as 06hrs</option>
                       </select>
                     </div>
                   </div>
                   <div className="relative cmv-search-container">
                     <label className="block text-xs font-medium text-gray-600 mb-1">
                       Buscar e Selecionar CMV
                     </label>
                     <input
                       type="text"
                       value={cmvFilters.search}
                       onChange={(e) => {
                         setCmvFilters({...cmvFilters, search: e.target.value});
                         if (e.target.value) {
                           setSelectedCMV(null); // Limpar seleção quando buscar novamente
                           setShowCMVList(true); // Mostrar lista quando começar a digitar
                         }
                       }}
                       onFocus={() => {
                         if (cmvFilters.search && !selectedCMV) {
                           setShowCMVList(true);
                         }
                       }}
                       placeholder={selectedCMV ? "CMV selecionado abaixo" : "Digite nome do motorista, placa ou prefixo..."}
                       className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                       disabled={!!selectedCMV} // Desabilitar campo quando CMV estiver selecionado
                     />
                     {isLoadingCMVs && (
                       <p className="mt-1 text-xs text-gray-500">Buscando...</p>
                     )}
                     
                     {/* Lista de CMVs abaixo do campo de busca */}
                     {!isLoadingCMVs && showCMVList && cmvFilters.search && cmvFilters.search.trim() && !selectedCMV && (
                       <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                         {cmvs.length === 0 ? (
                           <div className="px-3 py-2 text-sm text-gray-500">
                             Nenhum CMV encontrado com os filtros selecionados
                           </div>
                         ) : (
                           cmvs.map((cmv) => (
                             <div
                               key={cmv.id}
                               onClick={() => {
                                 setSelectedCMV(cmv.id);
                                 setSelectedMotorista(cmv.motorista_id);
                                 setCmvFilters({...cmvFilters, search: ''}); // Limpar campo de busca após seleção
                                 setShowCMVList(false); // Fechar lista após seleção
                               }}
                               className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors ${
                                 selectedCMV === cmv.id ? 'bg-blue-100' : ''
                               }`}
                             >
                               <div className="font-medium text-gray-900">
                                 CMV #{cmv.cmv} - {cmv.motorista_nome}
                               </div>
                               <div className="text-xs text-gray-600 mt-0.5">
                                 {cmv.veiculo_placa} {cmv.veiculo_prefixo ? `(${cmv.veiculo_prefixo})` : ''} | {cmv.turno} | {new Date(cmv.data).toLocaleDateString('pt-BR')}
                               </div>
                             </div>
                           ))
                         )}
                       </div>
                     )}
                   </div>
                   
                   {/* Informações do CMV selecionado */}
                   {selectedCMV && (() => {
                     const cmv = cmvs.find(c => c.id === selectedCMV);
                     if (!cmv) return null;
                     return (
                       <div className="mt-3 p-3 bg-blue-50 rounded-md border border-blue-200">
                         <div className="flex items-start justify-between">
                           <div className="flex-1">
                             <div className="font-semibold text-blue-900 mb-2 text-sm">CMV Selecionado:</div>
                             <div className="text-blue-700 text-xs space-y-1">
                               <div><strong>CMV:</strong> #{cmv.cmv}</div>
                               <div><strong>Motorista:</strong> {cmv.motorista_nome} {cmv.motorista_matricula ? `(${cmv.motorista_matricula})` : ''}</div>
                               <div><strong>Veículo:</strong> {cmv.veiculo_placa} {cmv.veiculo_prefixo ? `- ${cmv.veiculo_prefixo}` : ''} {cmv.veiculo_marca && cmv.veiculo_modelo ? `(${cmv.veiculo_marca} ${cmv.veiculo_modelo})` : ''}</div>
                               <div><strong>Turno:</strong> {cmv.turno} | <strong>Polo:</strong> {cmv.polo}</div>
                               <div><strong>Data:</strong> {new Date(cmv.data).toLocaleDateString('pt-BR')}</div>
                             </div>
                           </div>
                           <button
                             type="button"
                             onClick={() => {
                               setSelectedCMV(null);
                               setSelectedMotorista(0);
                               setCmvFilters({...cmvFilters, search: ''});
                             }}
                             className="text-red-600 hover:text-red-800 text-sm font-medium"
                           >
                             ✕ Remover
                           </button>
                         </div>
                       </div>
                     );
                   })()}
                 </div>
               </div>
              </div>
               
              {/* Botões fixos na parte inferior */}
              <div className="p-4 sm:p-6 pt-0 border-t border-gray-200 bg-white">
                {showMotoristaAssignPrompt ? (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                    <p className="text-sm text-green-900 mb-1 font-medium">
                      Motorista atribuído à fase {lastAssignedFaseNome} com sucesso!
                    </p>
                    <p className="text-sm text-green-800 mb-4">
                      {selectedOS.fases?.some((fase) => !fase.motorista)
                        ? 'Deseja atribuir motorista a outra fase ou fechar?'
                        : 'Todas as fases já têm motorista atribuído.'}
                    </p>
                    <div className="flex flex-col sm:flex-row justify-end gap-3">
                      <button
                        type="button"
                        onClick={closeMotoristaModal}
                        className="w-full sm:w-auto bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-800 transition-colors"
                      >
                        Salvar e fechar
                      </button>
                      {selectedOS.fases?.some((fase) => !fase.motorista) && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowMotoristaAssignPrompt(false);
                            setLastAssignedFaseNome(null);
                          }}
                          className="w-full sm:w-auto bg-white text-green-800 border border-green-300 px-4 py-2 rounded-lg hover:bg-green-100 transition-colors"
                        >
                          Salvar e continuar adicionando
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row justify-end gap-3">
                    <button
                      type="button"
                      onClick={closeMotoristaModal}
                      className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Fechar
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedFase && selectedCMV && selectedOS?.id) {
                          const cmv = cmvs.find(c => c.id === selectedCMV);
                          if (cmv) {
                            handleAtribuirMotorista(selectedOS.id, selectedFase, cmv.motorista_id);
                          } else {
                            toast.error('CMV selecionado não encontrado');
                          }
                        } else {
                          toast.error('Selecione uma fase e um CMV');
                        }
                      }}
                      disabled={!selectedFase || !selectedCMV || isAssigningMotorista}
                      className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isAssigningMotorista ? 'Atribuindo...' : 'Atribuir motorista'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
      )}
      {etiquetaOS && (
        <EtiquetasModal os={etiquetaOS} onClose={() => setEtiquetaOS(null)} />
      )}
    </div>
  );
};

export default OrdensServico;
